import {
	ClientActivityTypeEnum,
	ClientBondInterestTypeEnum,
	ClientCarBodyTypeEnum,
	ClientCarFuelEnum,
	ClientEventTypeEnum,
	ClientFinancialPlanExpenseNameEnum,
	ClientFinancialPlanExpenseTypeEnum,
	ClientIdentificationDocumentTypeEnum,
	ClientPortfolioDataCommodityServiceEnum,
	ClientPortfolioDomainEnum,
	ClientPortfolioInstructionHoldingTypeEnum,
	ClientPortfolioInsuredPersonRiskEnum,
	ClientPortfolioInvestmentEnterFeeAmountTypeEnum,
	ClientPortfolioInvestmentEnterFeeApplyingEnum,
	ClientPortfolioInvestmentEnterFeeTypeEnum,
	ClientPortfolioInvestmentFrequencyEnum,
	ClientPortfolioInvestmentTransactionEnum,
	ClientPortfolioInvestmentTypeEnum,
	ClientPortfolioPaymentFrequencyEnum,
	ClientPortfolioToolIdentificationTypeEnum,
	ClientPortfolioToolTypeEnum,
	ClientPropertyBuildingDispositionEnum,
	ClientPropertyBuildingMaterialEnum,
	ClientPropertyCarVehicleTypeEnum,
	ClientPropertyCategoryEnum,
	ClientPropertyOwnershipEnum,
	ClientPropertyRenovationEnum,
	ClientPropertyTypeEnum,
	ClientPropertyVehicleTypeEnum,
	CurrencyEnum,
	UserTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import type { MyPlannResourceDictionary } from "web_myplann/Infrastructure/Translations/ResourceDictionary";
import { EventClassificationEnum } from "web_myplann/NotificationCenter/EventAlert/types";
import englishZodTranslations from "zod-i18n-map/locales/en/zod.json";

export const MyPlannEnglishResourcesDictionary: MyPlannResourceDictionary = {
	Common: {
		AppName: "myPLANN but english",
		CurrencySign: "Kč",
		ValueNotProvided: "Value not provided",
		ValueNotProvidedSubtitle: "If you know the value, let your advisor know.",
		contact: "Contact",
		LoadMore: "Load older",
		save: "Save",
		continue: "Continue",
		ShowMore: "Show more",
		Send: "Send",
		Search: "Search",
		Filter: "Filter",
		Apply: "Apply",
		Select: "Select",
		Name: "Name",
		FirstName: "First name",
		NoData: "No data",
		ToDate: "To date",
		Min: "Min",
		Max: "Max",
		solvedEvents: "Solving events",
		upcomingEvents: "Upcoming events",
		overdueEvents: "Overdue events",
		resolvedEvents: "Resolved events",
		DisplayAll: "Display all",
		Hide: "Hide",
		LoadNext: "Load more",
		OneDocument: "document",
		FewDocuments: "documents",
		MoreDocuments: "documents",
		NoDocuments: "No documents",
		NotSpecified: "Not specified",
		DatePicker: {
			previousYear: "Previous year",
			nextYear: "Next year",
			previousMonth: "Previous month",
			nextMonth: "Next month",
			previousDecade: "Previous decade",
			nextDecade: "Next decade",
			chooseMonthAndYear: "{{title}}, choose month and year",
			chooseYear: "Year {{year}}, choose year",
		},
		ToolbarMenu: {
			WritteMessage: "Writte message",
			Call: "Call",
			WriteSMS: "Writte SMS",
			WriteEmail: "Writte E-mail",
		},
		somethingWentWrong: "Something went wrong, please contact your advisor",
		unknownError: "Unknown error",
		documentOpenFailed: "Failed to open document.",
		Cancel: "Cancel",
		Confirm: "Confirm",
		yes: "Yes",
		no: "No",
		ListBar: {
			itemsCount: "{{count}} items",
			itemsCount_one: "{{count}} item",
			itemsCount_few: "{{count}} items",
			itemsCount_other: "{{count}} items",
			filterChip: "Filter",
			filterChipActive: "Filter ({{summary}})",
		},
	},
	Errors: {
		ApplicationError: {
			Home: "Home",
			title: "Something went wrong",
			description:
				"We could not display this page. Please try again, or return to the start.",
			retry: "Try again",
			devDetails: "Error detail",
		},
		Error404: {
			Home: "Home",
			Subtitle: "Page not found",
		},
	},
	zod: englishZodTranslations,
	Profile: {
		title: "Profile",
		MissingAdvisor: {
			title: "You do not have an assigned advisor",
			description:
				"Your application has no source to load data from. Please contact your advisor.",
			logOut: "Log out",
		},
		Fields: {
			phone: "Phone",
			email: "E-mail",
		},
		Overview: {
			accountSection: "Account",
			toolsSection: "Tools",
			name: "Full name",
			address: "Address",
			birthDate: "Date of birth",
			documents: "My documents",
			logOut: "Log out",
		},
		Sections: {
			PersonalDataAndAccounts: "Personal data and accounts",
			Addresses: "Addresses",
			Paperwork: "Documents",
			FinancialBalance: "Financial balance",
		},
		EmptyList: {
			Addresses: {
				label: "No addresses",
				description: "Here you will see all of your addresses.",
			},
			Paperwork: {
				label: "No paperwork",
				description: "Here you will see all of your paperwork.",
			},
		},
	},
	PersonalDataAndAccounts: {
		title: "Personal data",
		PersonalData: {
			title: "Personal data",
			Name: "Name",
			Surname: "Surname",
			BirthNumber: "Birth number",
			BirthDate: "Birth date",
			BirthPlace: "Birth place",
			MaritalStatus: "Marital status",
			HealthInsurance: "Health insurance",
		},
	},
	Addresses: {
		title: "Addresses",
		Type: {
			permanent: "Permanent",
			contact: "Contact",
		},
		PermanentAddress: {
			title: "Permanent address",
			Street: "Street",
			StreetNumber: "Description and reference number",
			City: "City",
			Zip: "ZIP",
			Country: "Country",
		},
		ContactAddress: {
			title: "Contact address",
			Street: "Street",
			StreetNumber: "Desc. and ref. number",
			City: "City",
			Zip: "ZIP",
			Country: "Country",
		},
	},
	Reminder: {
		title: "Reminder",
		add: "Add reminder",
		quickChoice: "Quick choice",
		date: "Date",
		selectDate: "Select date",
		recurrence: "Repeat",
		until: "until {{date}}",
		Recurrence: {
			once: "Once",
			yearly: "Every year",
			twoYears: "Every 2 years",
			fourYears: "Every 4 years",
		},
		Presets: {
			monthBefore: "A month before expiry",
			threeMonthsBefore: "3 months before",
			onExpiry: "On expiry date",
		},
	},
	Paperwork: {
		title: "My documents",
		Types: {
			[ClientIdentificationDocumentTypeEnum.Passport]: "Passport",
			[ClientIdentificationDocumentTypeEnum.IdentityCard]: "Identity card",
			[ClientIdentificationDocumentTypeEnum.WeaponLicense]: "Weapon license",
			[ClientIdentificationDocumentTypeEnum.STK]: "STK protocol",
			[ClientIdentificationDocumentTypeEnum.DrivingLicense]: "Driving license",
			[ClientIdentificationDocumentTypeEnum.FishingLicense]: "Fishing license",
			[ClientIdentificationDocumentTypeEnum.HuntingLicense]: "Hunting license",
			[ClientIdentificationDocumentTypeEnum.ISIC]: "ISIC card",
			[ClientIdentificationDocumentTypeEnum.InsuranceCard]: "Insurance card",
			[ClientIdentificationDocumentTypeEnum.Other]: "Other document",
			[ClientIdentificationDocumentTypeEnum.PilotLicense]: "Pilot license",
			[ClientIdentificationDocumentTypeEnum.ResidencePermit]:
				"Residence permit",
			[ClientIdentificationDocumentTypeEnum.ShipLicense]: "Skipper's license",
			[ClientIdentificationDocumentTypeEnum.TravelLicense]: "Travel license",
		},
		DocumentNumber: "Document number",
		ExpirationDate: "Expiration date",
		uploadedSection: "Uploaded documents",
		numberPrefix: "no.",
		addDocument: "Add another document",
		infoNote:
			"Documents are stored encrypted and accessible only to you and your advisor. The system will notify you before they expire.",
		Validity: {
			until: "until {{date}}",
			noExpiration: "No expiration",
		},
		Detail: {
			type: "Document type",
			number: "Document number",
			issuer: "Issued by",
			validTo: "Valid until",
			Status: {
				valid: "Valid",
				expiring: "Expiring soon",
				expired: "Expired",
				none: "No expiration",
			},
			Actions: {
				title: "Actions",
				update: "Update document",
				updateHint: "Edit the document details",
				delete: "Delete document",
			},
			DeleteConfirm: {
				title: "Delete document?",
				description:
					"The document will be permanently removed. This action cannot be undone.",
				confirm: "Delete",
				cancel: "Cancel",
			},
			Error: {
				title: "Failed to load documents",
				description: "Check your internet connection and try again.",
				retry: "Try again",
			},
		},
		Upload: {
			title: "Add document",
			updateTitle: "Update document",
			Categories: {
				personal: "Personal documents",
				vehicles: "Vehicles & transport",
				sport: "Hobbies & sport",
				insurance: "Insurance",
				other: "Other",
			},
			Form: {
				sectionTitle: "Document details",
				type: "Document type",
				number: "Document number",
				validTo: "Valid until",
				noExpiry: "No expiration date",
				selectDate: "Select date",
				customName: "Document name",
				customNamePlaceholder: "Weapon license, EHIC, ...",
				note: "Note (optional)",
				notePlaceholder: "optional",
				submit: "Save document",
			},
			Success: {
				title: "Document saved!",
				updateTitle: "Document updated",
				description: "{{name}} was successfully added to your documents.",
				updateDescription: "{{name}} details were successfully updated.",
				reminderSet: "A reminder has been set.",
				expiryNote: "The system will notify you before it expires.",
				viewInWallet: "View documents",
				viewDocument: "View document",
				addAnother: "Add another document",
				back: "Back",
			},
		},
	},
	Advisor: {
		title: "My advisor",
		call: "Call",
		messages: "Messages",
		mainBadge: "Primary",
		pickerTitle: "Your advisors",
		introduction: "Introduction",
		showFull: "Show all",
		emailAria: "Email {{email}}",
		addressAria: "Open {{address}} in maps",
		linkedinAria: "Open LinkedIn profile",
		websiteAria: "Open website",
		makeMainAria: "Set {{name}} as the primary advisor",
		pickerAria: "Your advisors, pending requests: {{count}}",
		deleteAria: "Remove {{name}}",
		pendingHeading: "Connection requests ({{count}})",
		connectedHeading: "Connected advisors",
		approve: "Approve",
		refuse: "Refuse",
		keep: "Keep",
		unreadMessages: "{{count}} unread messages",
		unreadMessages_one: "{{count}} unread message",
		unreadMessages_few: "{{count}} unread messages",
		unreadMessages_other: "{{count}} unread messages",
		specializations: {
			investments: "investments",
			life_insurance: "life insurance",
			non_life_insurance: "non-life insurance",
			mortgages: "mortgages",
			consumer_loans: "consumer loans",
			retirement: "retirement",
			comprehensive: "comprehensive advice",
		},
		mainConfirm: {
			title: "Set primary advisor",
			description:
				"Do you really want to set {{name}} as your primary advisor?",
			confirm: "Set as primary",
		},
		deleteConfirm: {
			advisorTitle: "Remove advisor",
			assistantTitle: "Remove assistant",
			advisorDescription: "Do you want to remove advisor {{name}}?",
			advisorSharedDescription:
				"Do you want to remove advisor {{name}}? You will lose the contracts, property and services this advisor shared with you.",
			assistantDescription: "Do you want to remove assistant {{name}}?",
			assistantSharedDescription:
				"Do you want to remove assistant {{name}}? You will lose the contracts, property and services this assistant shared with you.",
			advisorConfirm: "Remove advisor",
			assistantConfirm: "Remove assistant",
		},
		refuseConfirm: {
			title: "Refuse request",
			description:
				"{{name}} will not connect with you and the request will be removed.",
			confirm: "Refuse request",
		},
		loadError: {
			title: "Your advisor could not be loaded",
			description:
				"Something went wrong while loading the data. Please try again.",
			retry: "Try again",
		},
	},
	Chat: {
		title: "Messages",
		inputPlaceholder: "Write a message...",
		online: "Online",
		today: "Today",
		yesterday: "Yesterday",
		loadOlder: "Load older messages",
		emptyTitle: "No messages yet",
		emptyDescription: "Send your advisor the first message.",
		sendError: "The message could not be sent.",
		retry: "Send again",
		attachmentImage: "Photo",
		attachmentFile: "Document",
		searchPlaceholder: "Search conversations...",
		newMessage: "New message",
		youPrefix: "You:",
		listEmptyTitle: "No messages yet",
		listEmptyDescription:
			"Write to your advisor — they usually reply within a few hours.",
		searchEmptyTitle: "Nothing found",
		searchEmptyDescription: "Try adjusting your search term or advisor name.",
		clearSearch: "Clear search",
		writeToAdvisor: "Write to advisor",
	},
	Settings: {
		title: "More",
		DarkMode: "Dark mode",
		Language: "Language",
		LogOut: "Log out",
		RemoveAccount: "Remove account",
		AppVersion: "App version",
		LanguageOptions: {
			Czech: "Čeština",
			English: "English",
		},
		Sections: {
			Tools: "Tools",
			Settings: "Settings",
			Account: "Account",
		},
		Items: {
			FinancialBalance: "Financial balance",
			MyDocuments: "My documents",
			Activities: "Activities",
			Calculators: "Calculators",
			Settings: "Settings",
			Security: "Security",
		},
		Copyright: "© 2026 SAB servis s.r.o.",
	},
	Security: {
		title: "Security",
		Biometry: "Biometry",
		BiometryError: "Failed to change biometry settings",
		Sections: {
			Login: "Login",
		},
	},
	Dashboard: {
		TitleCard: {
			title: "Detail",
			tooltip:
				"The overview is based on data provided by the financial institution or advisor. If you want to verify the accuracy of the data, please contact your advisor.",
			zeroValue:
				"There are <b>{{count}}</b> items here without a value. Chat with your advisor to fill in the details.",
			zeroValue_one:
				"There is <b>{{count}}</b> item here without a value. Chat with your advisor to fill in the details.",
			zeroValue_few:
				"There are <b>{{count}}</b> items here without a value. Chat with your advisor to fill in the details.",
			zeroValue_other:
				"There are <b>{{count}}</b> items here without a value. Chat with your advisor to fill in the details.",
			netValue: "net worth: {{value}}",
			netValueTooltip:
				"The value after deducting your liabilities (loans, mortgages, leasing). If your assets are not complete, contact your advisor.",
		},
		Empty: {
			label: "No content",
			description:
				"The client has no reporting data available. Enable reporting in the client detail using the “Zobrazit reporting v myPLANN” switch.",
		},
		EmptyState: {
			eyebrow: "This is your myPLANN",
			title: "Your finances in one place",
			description:
				"Contracts, documents, goals – the more you keep here, the clearer the picture.",
			addContractButton: "Add contract",
			uploadDocumentButton: "Upload document",
			addGoalButton: "Add goal",
			contractMessage:
				"I'd like to add a contract to my portfolio. How should I proceed?",
			documentMessage:
				"I'd like to upload a document to my folder. Can you help me with that?",
		},
		Detail: {
			back: "Back",
		},
		OverviewType: {
			Total: "Total assets",
			Cash: "Cash",
			Investment: "Investment",
			InvestmentProperty: "Real estate",
			Liabilities: "Liabilities",
			Other: "Other",
		},
		NotificationSections: {
			ResolvingNow: "Currently solving",
			Upcoming: "Coming up",
			Recent: "Recently happened",
			ShowAll: "Show all",
		},
		ImageCard: {
			title: "Might be useful",
			Mortgage: {
				title: "Simple mortgage calculator on a few touches",
				description: "Compare offers online or use our advisor",
			},
			Travel: {
				title: "Select a suitable trip",
				description: "Select a suitable trip and fill out the form",
			},
		},
		Inspiration: {
			title: "Inspiration",
			seeAll: "All inspirations",
			Cards: {
				ownHome: "Your own roof over your head",
				earlyFreedom: "Financial independence",
			},
		},
		QuickCalculators: {
			title: "Arrange it yourself",
			Items: {
				travelInsurance: "Travel insurance",
				propertyInsurance: "Property insurance",
				carMotoInsurance: "Car insurance",
				consumerLoan: "Consumer loan",
				mortgage: "Mortgage",
				pensionAnnuity: "Investment",
			},
		},
		FinancialBalance: {
			title: "Financial balance",
			subtitlePositive: "Monthly you have on average",
			subtitleNegative: "Monthly you are missing on average",
			descriptionPositive:
				"Would you like to make the most of your budget surplus? Contact your advisor and start building a secure future today.",
			descriptionNegative:
				"Get in touch with your advisor and work together to find a way to improve your balance sheet.",
			startButton: "Start",
			clientNotFound:
				"Client was not found under the selected advisor, therefore you cannot view the financial balance. Please contact your advisor.",
			income: "Income",
			expense: "Expenses",
			balance: "Balance",
			detail: "Detail",
			emptyTitle: "How much is left each month?",
			emptyDescription:
				"5 minutes and you'll know. Tell us your income and expenses, we'll calculate the rest.",
			emptyDuration: "5 minutes",
			emptyCta: "I want to know",
			healthGreat: "Great",
			healthGood: "Good",
			healthTight: "Tight",
			healthWarning: "Caution",
			healthNegative: "In the red",
			tipPositive:
				"Great! You have {{amount}} free each month. How about investing?",
			tipNegative:
				"Your expenses exceed your income. Discuss it with your advisor.",
			tipCtaPositive: "How to use the money →",
			tipCtaNegative: "Discuss with advisor →",
			Modal1: {
				title: "Financial balance",
				description:
					"If you need to discuss the best way to work with the result, click the “I'm interested” button and this information will be sent to your advisor.",
				button: "I'm interested",
			},
			Modal2: {
				title: "Thank you for your interest",
				description:
					"Your advisor will contact you and suggest the best possible solution.",
			},
			contactAdvisorButton: "Contact advisor",
		},
		Sections: {
			todo: "To solve",
			upcoming: "Upcoming",
			past: "Already happened",
		},
		RelativeDate: {
			today: "Today",
			future: "In {{count}} days",
			future_one: "In {{count}} day",
			future_few: "In {{count}} days",
			future_other: "In {{count}} days",
			past: "{{count}} days ago",
			past_one: "{{count}} day ago",
			past_few: "{{count}} days ago",
			past_other: "{{count}} days ago",
		},
		ShowAllButton: {
			title: "Show all",
		},
		UserType: {
			[UserTypeEnum.Client]: "Client",
			[UserTypeEnum.Agent]: "Advisor",
			[UserTypeEnum.Assistant]: "Assistant",
		},
		EmptyList: {
			label: "No content",
			description:
				"Your portfolio overview will appear once your advisor prepares and shares it.",
		},
	},
	Task: {
		Detail: {
			title: "Task",
			notFoundToast: "This task no longer exists",
			BasicInfo: {
				title: "Basic info",
				Name: "Name",
				Date: "Date",
				CreatedBy: "Created by",
				Solver: "Solver",
				Desc: "Description",
			},
			Process: {
				title: "Process",
			},
		},
	},
	Activity: {
		Detail: {
			title: "Activity",
			notFoundToast: "This activity no longer exists",
			BasicInfo: {
				title: "Basic info",
				Name: "Name",
				Type: "Activity type",
				State: "State",
				Date: "Date",
				Address: "Address",
				CreatedBy: "Created by",
			},
			Participants: {
				title: "Participants",
				client: "Client",
			},
			Agents: {
				advisor: "Advisor",
				assistant: "Assistant",
			},
			OtherClient: "Other client",
			notes: "Entries and notes",
			ToolbarMenu: {
				WritteMessage: "Writte message",
				Call: "Call",
				WritteSMS: "Writte SMS",
				WritteEmail: "Writte E-mail",
			},
		},
		List: {
			title: "Activities",
			NoTask: "We are not currently working on any tasks.",
			NoContent: "No content",
			NoActivities:
				"Here you will see all past or scheduled activities with your advisor.",
			NoUpcomingEvents: "No upcoming events",
			NoOverdueEvents: "No overdue events",
			NoResolvedEvents: "No resolved events",
			EventType: {
				[ClientEventTypeEnum.Task]: "Task",
				[ClientEventTypeEnum.Activity]: "Activity",
			},
			ActivityType: {
				[ClientActivityTypeEnum.Call]: "Call",
				[ClientActivityTypeEnum.Meeting]: "Meeting",
				[ClientActivityTypeEnum.Other]: "Other",
				[ClientActivityTypeEnum.Administration]: "Administration",
			},
			Task: "Task",
			From: "from",
			To: "to",
		},
	},
	Portfolio: {
		Chart: {
			Value: "Value",
			Invested: "Invested",
			InvestedTogether: "Total invested",
			RemainsToPay: "Remains to pay",
			Paid: "Paid",
			Date: "Date",
			To: "To",
			Tooltip:
				"The overview is based on data from financial institutions, or provided by the advisor. For verification and more information, please contact your advisor.",
			ReportingDataUnavailable:
				"Not all data required to display the chart is currently available.",
		},
		Titles: {
			document: "Documents",
			portfolio: "Portfolio",
		},
		List: {
			BookmarkTileTitle: "Is the contract missing here?",
			BookmarkTileDesc:
				"Add the missing contract here so you have all of them in one place.",

			Tabs: {
				portfolio: "Portfolio",
				documents: "Documents",
			},
			Sorter: {
				title: "Sort by",
				Types: {
					default: "Areas",
					producer: "Provider",
					date: "Signing date",
				},
			},
			AddDocumentDrawer: {
				title: "Add document",
				pickError: "The file could not be loaded. Please try again.",
				uploadError:
					"The document could not be uploaded. Check your connection and try again.",
				Types: {
					photo: "Photo new document",
					gallery: "Upload document from gallery",
					file: "Upload document from files",
				},
			},
			FolderActionsDrawer: {
				title: "Folder actions",
				Actions: {
					rename: "Rename",
				},
			},
			DocumentActionsDrawer: {
				title: "Document actions",
				fileDrawerTitle: "Move to folder",
				folderDrawerTitle: "Folder actions",
				Actions: {
					download: "Download",
					removeDownload: "Remove from downloaded",
					rename: "Rename",
					delete: "Delete",
					share: "Share",
					move: "Move to folder",
				},
				Rename: {
					fileRename: "Document name",
					folderRename: "Folder name",
				},
			},
			MoveDocumentDialog: {
				title: "Move document",
				Actions: {
					create: "Create new folder",
				},
			},
			DeleteDocumentDialog: {
				title: "Delete document?",
				description:
					"Are you sure you want to delete the document {{documentName}}?",
				confirm: "Delete",
				cancel: "Cancel",
			},
			PortfolioDomainType: {
				[ClientPortfolioDomainEnum.BankingProducts]: "Banking products",
				[ClientPortfolioDomainEnum.BuildingSavings]: "Building savings",
				[ClientPortfolioDomainEnum.BusinessLoans]: "Business loan",
				[ClientPortfolioDomainEnum.Commodities]: "Commodities",
				[ClientPortfolioDomainEnum.Cryptocurrencies]: "Cryptocurrencies",
				[ClientPortfolioDomainEnum.Investments]: "Investments",
				[ClientPortfolioDomainEnum.Leasing]: "Leasing",
				[ClientPortfolioDomainEnum.LifeInsurance]: "Life insurance",
				[ClientPortfolioDomainEnum.Loans]: "Loans",
				[ClientPortfolioDomainEnum.PaidAdvisory]: "Paid advisory",
				[ClientPortfolioDomainEnum.PensionProducts]: "Pension products",
				[ClientPortfolioDomainEnum.PropertyInsurance]: "Property insurance",
				[ClientPortfolioDomainEnum.Services]: "Services",
				Uncategorized: "Uncategorized",
			},
			PortfolioDomainAbbreviation: {
				[ClientPortfolioDomainEnum.LifeInsurance]: "LI",
				[ClientPortfolioDomainEnum.PropertyInsurance]: "NL",
				[ClientPortfolioDomainEnum.PensionProducts]: "PP",
				[ClientPortfolioDomainEnum.BuildingSavings]: "BS",
				[ClientPortfolioDomainEnum.Investments]: "I",
				[ClientPortfolioDomainEnum.BusinessLoans]: "BL",
				[ClientPortfolioDomainEnum.Loans]: "LN",
				[ClientPortfolioDomainEnum.Leasing]: "L",
				[ClientPortfolioDomainEnum.Commodities]: "CM",
				[ClientPortfolioDomainEnum.Cryptocurrencies]: "CR",
				[ClientPortfolioDomainEnum.BankingProducts]: "BP",
			},
			PortfolioSubType: {
				[ClientPropertyTypeEnum.AutoMoto]: "CAR/MOTO",
				[ClientPropertyTypeEnum.Flat]: "FLAT",
				[ClientPropertyTypeEnum.House]: "HOUSE",
				[ClientPropertyTypeEnum.Cottage]: "COTTAGE",
				[ClientPropertyTypeEnum.Land]: "LAND",
				[ClientPropertyTypeEnum.Art]: "ART",
				[ClientPropertyTypeEnum.Commodity]: "COMMODITY",
				[ClientPortfolioDomainEnum.LifeInsurance]: "LIFE INSURANCE",
				[ClientPortfolioDomainEnum.PropertyInsurance]: "NON-LIFE INSURANCE",
				[ClientPortfolioDomainEnum.PensionProducts]: "PENSION PRODUCT",
				[ClientPortfolioDomainEnum.BuildingSavings]: "BUILDING SAVINGS",
				[ClientPortfolioDomainEnum.Investments]: "INVESTMENT",
				[ClientPortfolioDomainEnum.BusinessLoans]: "BUSINESS LOAN",
				[ClientPortfolioDomainEnum.Loans]: "LOAN",
				[ClientPortfolioDomainEnum.Leasing]: "LEASING",
				[ClientPortfolioDomainEnum.Commodities]: "INVESTMENT COMMODITY",
				[ClientPortfolioDomainEnum.BankingProducts]: "BANK PRODUCT",
				[ClientPortfolioDomainEnum.Services]: "SERVICE",
				[ClientPortfolioDomainEnum.PaidAdvisory]: "PAID ADVISORY",
				[ClientPortfolioDomainEnum.Cryptocurrencies]: "CRYPTOCURRENCY",
			},
			Property: "Property",
			Skeleton: {
				label: "No content",
				description:
					"This page has no data, any available portfolio will be displayed here.",
			},
			Redesign: {
				addItem: "Add contract or asset",
				selectedCategoriesCount: "{{count}} types",
				selectedCategoriesCount_one: "{{count}} type",
				selectedCategoriesCount_few: "{{count}} types",
				selectedCategoriesCount_other: "{{count}} types",
				investmentBadge: "Investment",
				Categories: {
					financialProducts: "Financial products",
					assets: "Assets",
					services: "Services",
				},
				PaymentFrequencyShort: {
					[ClientPortfolioPaymentFrequencyEnum.None]: "",
					[ClientPortfolioPaymentFrequencyEnum.Single]: "once",
					[ClientPortfolioPaymentFrequencyEnum.Yearly]: "yr",
					[ClientPortfolioPaymentFrequencyEnum.Semiannually]: "6mo",
					[ClientPortfolioPaymentFrequencyEnum.Quarterly]: "qtr",
					[ClientPortfolioPaymentFrequencyEnum.Monthly]: "mo",
				},
				Sort: {
					oblast: "Area",
					name: "Name",
					producer: "Provider",
					value: "Value",
				},
				EmptyState: {
					headline: "Everything in one place",
					description:
						"Add your contracts and assets to get a complete overview of your finances.",
					primaryCta: "Add first contract",
					advisorTitle: "An advisor can help",
					advisorDescription:
						"Write to us and we'll fill in your portfolio together.",
					PreviewCards: {
						insuranceLabel: "Insurance",
						insuranceSub: "Life, property, travel",
						investmentLabel: "Investments",
						investmentSub: "Funds, pension, savings",
						realEstateLabel: "Real estate",
						realEstateSub: "Mortgages, housing, investment",
						assetsLabel: "Assets",
						assetsSub: "Vehicles, valuables",
					},
				},
				Filter: {
					title: "Filter",
					categoriesLabel: "Product type",
					sortLabel: "Sorting",
					ascending: "Ascending",
					descending: "Descending",
					reset: "Reset",
					apply: "Apply filters",
					activeSummaryLabel: "Active filters",
				},
				Documents: {
					addDocument: "Upload document",
					documentsCount: "{{count}} documents",
					EmptyState: {
						headline: "Your digital binder",
						description:
							"Secure storage for all your contracts and documents. Always at hand, from any device.",
						primaryCta: "Upload first document",
						trustBadge: "Encrypted storage at banking standard",
						Steps: {
							photoLabel: "Photograph the contract",
							photoDesc: "Just a photo from your phone",
							aiLabel: "AI recognizes the data",
							aiDesc: "Automatically extracts the key details",
							organizedLabel: "Everything organized",
							organizedDesc: "Documents sorted by contract",
						},
					},
				},
			},
		},
		AddItem: {
			title: "What are you adding?",
			Sections: {
				insurance: "Insurance",
				savings: "Savings and investments",
				loans: "Loans",
				property: "Property",
				other: "Other",
			},
			Items: {
				lifeInsurance: "Life insurance",
				propertyInsurance: "Property insurance",
				householdInsurance: "Household insurance",
				liabilityInsurance: "Liability insurance",
				vehicleInsurance: "Vehicle insurance",
				travelInsurance: "Travel insurance",
				businessInsurance: "Business insurance",
				accidentInsurance: "Accident insurance",
				investment: "Investment",
				pension: "Pension product",
				buildingSavings: "Building savings",
				bankProduct: "Bank product",
				crypto: "Crypto",
				commodity: "Commodities",
				mortgage: "Mortgage",
				consumerLoan: "Consumer loan",
				businessLoan: "Business loan",
				leasing: "Leasing",
				auto: "Car/moto",
				propertyItem: "Real estate",
				art: "Art",
				service: "Service",
			},
		},
		EditProperty: {
			title: "Edit property",
			submit: "Save changes",
			savedToast: "{{type}} item saved",
		},
		EditPortfolio: {
			title: "Edit contract",
			serviceTitle: "Edit service",
			submit: "Save changes",
			savedToast: "{{type}} item saved",
			UnsavedChanges: {
				title: "Unsaved changes",
				description: "Your changes have not been saved yet.",
				discard: "Discard",
			},
		},
		Actions: {
			editContract: "Edit contract",
			editProperty: "Edit property",
			editService: "Edit service",
			updateValue: "Update value",
			deleteContract: "Delete contract",
			DeleteProperty: {
				title: "Delete property?",
				descriptionPrefix: "Are you sure you want to delete ",
				descriptionSuffix: "? This action cannot be undone.",
				confirm: "Delete",
				cancel: "Keep",
				successToast: "Property deleted",
			},
			DeletePortfolio: {
				title: "Delete contract?",
				descriptionPrefix: "Are you sure you want to delete ",
				descriptionSuffix: "? This action cannot be undone.",
				confirm: "Delete",
				cancel: "Keep",
				successToast: "Contract deleted",
			},
		},
		AddShared: {
			successTitle: "Done",
			successHeadline: "Added to portfolio!",
			successDescription: "The item is immediately visible in your portfolio.",
			successHint:
				"Your advisor has been notified and can fill in the missing details.",
			successAddAnother: "Add another",
			successBackToPortfolio: "Back to portfolio",
			submit: "Add to portfolio",
			Placeholders: {
				fill: "Fill in",
				optional: "Optional",
				searchOrEnterProducer: "Search or enter your own producer",
				searchOrEnterProduct: "Search or enter your own product",
				select: "Select",
			},
			Combobox: {
				useValue: 'Use "{{value}}"',
			},
			Validations: {
				required: "Required field",
				maxLength: "Maximum length is {{max}} characters.",
				positiveNumber: "Value must be a positive number.",
				positiveInteger: "The value must be a positive whole number.",
				maxValue: "Value is too high.",
				startBeforeSigned: "Start date cannot be before the signature date.",
				endBeforeSigned: "End date cannot be before the signature date.",
				endBeforeStart: "End date cannot be before the start date.",
				dateInFuture: "Date cannot be in the future.",
				valueRequiredWithInvested:
					"Total invested can only be saved together with the current value.",
				fixationEndBeforeStart: "Fixation end cannot be before the start date.",
				requiredMinimum:
					"Fill in at least one of the fields {{fields}} or {{lastField}} — otherwise the item cannot be recognised in your portfolio.",
			},
		},
		AddCar: {
			title: "Car/moto",
			clientBannerTextBefore: "This item will be marked as ",
			clientBannerBadge: "Client",
			clientBannerTextAfter:
				", your advisor will see it and fill in what's missing.",
			Sections: {
				identification: "Identification",
				valueAndDates: "Value and dates",
				engine: "Engine",
				description: "Additional description",
			},
			Fields: {
				model: "Model",
				licensePlate: "Registration plate (RZ)",
				manufactureMonthYear: "Month/year of manufacture",
				description: "Additional description",
			},
			Placeholders: {
				select: "Select",
			},
			Validations: {
				positiveInteger: "Value must be a positive integer.",
				vinFormat: "VIN must be exactly 17 alphanumeric characters.",
				monthYearFormat: "Format must be MM/YYYY or YYYY.",
				notInFuture: "Date cannot be in the future.",
			},
		},
		AddArt: {
			title: "Art",
			Sections: {
				basicInfo: "Basic information",
				description: "Additional description",
				investment: "Classification",
			},
			Fields: {
				itemTitle: "Item label",
				shortDescription: "Name / description",
				purchaseDate: "Purchase date",
				description: "Additional description",
				value: "Current value",
				forInvestments: "Investment asset",
			},
			Validations: {
				notInFuture: "Date cannot be in the future.",
			},
		},
		EditLand: {
			submit: "Save changes",
			savedToast: "{{type}} item saved",
		},
		AddRealEstate: {
			title: "Real estate",
			submit: "Add to portfolio",
			typeLabel: "Property type",
			typeDrawerTitle: "Select type",
			successTitle: "Done",
			successHeadline: "Added to portfolio!",
			successDescription: "The item is immediately visible in your portfolio.",
			successHint:
				"Your advisor has been notified and can complete the details.",
			successAddAnother: "Add another",
			successBackToPortfolio: "Back to portfolio",
			Types: {
				Land: "Land",
				Flat: "Flat",
				House: "House",
				Cottage: "Other building",
			},
			Sections: {
				location: "Address",
				parameters: "Parameters",
				amenities: "Amenities",
				valueAndDates: "Value and dates",
				description: "Additional description",
			},
			Fields: {
				description: {
					Flat: "Additional flat description",
					House: "Additional house description",
					Cottage: "Additional building description",
				},
			},
			Placeholders: {
				fill: "Fill in",
				optional: "Optional",
			},
			Validations: {
				requiredMinimum:
					"Fill in the street, municipality, ZIP code or description — they identify the property in your portfolio.",
				maxLength: "Maximum length is {{max}} characters.",
				zipFormat: "ZIP code must have 5 characters.",
				positiveInteger: "The value must be a positive integer.",
				maxValue: "The value is too high.",
			},
		},
		EditRealEstate: {
			submit: "Save changes",
			savedToast: "{{type}} item was saved",
		},
		AddLand: {
			title: "Land",
			Sections: {
				basicInfo: "Basic information",
				location: "Address",
				valueAndDates: "Value and dates",
				description: "Additional description",
			},
			Fields: {
				name: "Name",
				location: "Street and number",
				locationMunicipality: "Municipality",
				locationZipCode: "ZIP code",
				parcelNumber: "Plot number",
				landSize: "Land area",
				value: "Current price",
				purchaseDate: "Purchase date",
				description: "Additional description",
			},
			Validations: {
				zipFormat: "ZIP code must have 5 characters.",
				positiveInteger: "Value must be a positive integer.",
				notInFuture: "Date cannot be in the future.",
			},
		},
		AddBuildingSavings: {
			title: "Building savings",
			Sections: {
				basicInfo: "Basic information",
				values: "Values",
				additionalInfo: "Additional information",
				note: "Note",
			},
			Fields: {
				name: "Contract name",
				contractNumber: "Contract number",
				producer: "Provider",
				product: "Product name",
				productCategory: "Product category",
				signedAt: "Signed at",
				startAt: "Start date",
				endAt: "End date",
				payment: "Regular deposit",
				paymentFrequency: "Payment frequency",
				appreciation: "Appreciation rate",
				targetAmount: "Target amount",
				stateContribution: "State contribution",
				note: "Note",
			},
		},
		AddCommodities: {
			title: "Commodities",
			Placeholders: {
				name: "e.g. Gold savings",
			},
		},
		AddBankProduct: {
			title: "Bank product",
			Placeholders: {
				name: "e.g. Fio savings account",
			},
			Fields: {
				signedAt: "Opened on",
			},
		},
		AddPension: {
			title: "Pension product",
			Sections: {
				basicInfo: "Basic information",
				values: "Values",
				additionalInfo: "Additional information",
				note: "Note",
			},
			Fields: {
				name: "Contract name",
				contractNumber: "Contract number",
				producer: "Provider",
				product: "Product name",
				productCategory: "Product category",
				signedAt: "Signed at",
				startAt: "Start date",
				endAt: "End date",
				payment: "Regular deposit",
				paymentFrequency: "Payment frequency",
				note: "Note",
			},
		},
		AddTools: {
			Sections: {
				investmentTools: "Investment instruments",
				instructions: "Instructions",
			},
			Fields: {
				toolType: "Instrument type",
				identificationType: "Identification type",
				identification: "Identification",
				commodity: "Commodity",
				cryptocurrency: "Cryptocurrency",
				holdingType: "Holding type",
				name: "Name",
				accountName: "Account name",
				invested: "Total invested",
				value: "Current value",
				valueDate: "As of",
				instructionType: "Type",
				startAt: "Start date",
				endAt: "End date",
				amount: "Amount",
				currency: "Currency",
				frequency: "Frequency",
				count: "Quantity",
				interestRate: "Interest rate",
			},
			Actions: {
				addTool: "Add investment instrument",
				addInstruction: "Add instruction",
				createTool: "Add tool",
				saveChanges: "Save changes",
				cancel: "Cancel",
				removeTool: "Remove instrument",
				removeInstruction: "Remove instruction",
			},
			Drawers: {
				tool: "Investment instrument",
				instruction: "Instruction",
			},
			IdentificationType: {
				Isin: "ISIN",
				Other: "Other",
			},
			Identification: {
				zlato: "Gold",
				stribro: "Silver",
				platina: "Platinum",
				palladium: "Palladium",
			},
			DuplicateIsin: {
				title: "This ISIN is already here",
				description:
					"You already have instrument {{isin}} on the contract. After merging, the instructions of both will be under one instrument.",
				descriptionWithName:
					"You already have instrument {{isin}} on the contract as {{name}}. After merging, the instructions of both will be under one instrument.",
				confirm: "Merge",
				cancel: "Cancel",
			},
			isinWarning:
				"An ISIN has 12 characters: 2 letters, 9 letters or digits and a check digit. You can still save it.",
			instructionsRequireTool:
				"Fill in the instrument name or identification before adding an instruction.",
			commoditySearch: "Pick a commodity or type your own",
			bondInstructionsHint:
				"Instructions for this bond are managed by your advisor.",
			instructionSummary: "Instructions: {{count}}",
			previousValues: "Previous values",
			deleteValue: "Delete value {{value}} as of {{date}}",
			updateValue: "Update value",
			done: "Done",
		},
		AddInvestment: {
			title: "Investment",
			Placeholders: {
				name: "e.g. Conseq conservative fund",
			},
			Sections: {
				basicInfo: "Basic information",
				values: "Values",
				showValues: "Show values",
				hideValues: "Hide values",
				additionalInfo: "Additional information",
				note: "Note",
			},
			Fields: {
				name: "Contract name",
				contractNumber: "Contract number",
				producer: "Provider",
				product: "Product",
				value: "Current value",
				invested: "Total invested",
				valueDate: "As of",
				signedAt: "Signed at",
				startAt: "Start date",
				endAt: "End date",
				note: "Note",
			},
		},
		AddLifeInsurance: {
			title: "Life insurance",
			Sections: {
				basicInfo: "Basic information",
				additionalInfo: "Additional information",
			},
			Fields: {
				name: "Contract name",
				contractNumber: "Contract number",
				producer: "Provider",
				product: "Product name",
				category: "Product category",
				signedAt: "Signed at",
				startAt: "Start date",
				endAt: "End date",
			},
		},
		AddNonLifeInsurance: {
			Titles: {
				property: "Property insurance",
				household: "Household insurance",
				liability: "Liability insurance",
				vehicle: "Vehicle insurance",
				travel: "Travel insurance",
				business: "Business insurance",
				accident: "Accident insurance",
			},
			NamePlaceholders: {
				property: "e.g. Flat insurance in Zbraslav",
				household: "e.g. Household flat Prague",
				liability: "e.g. what the policy covers",
				vehicle: "e.g. Octavia liability insurance",
				travel: "e.g. Year-round travel cover",
				business: "e.g. Business premises insurance",
				accident: "e.g. Kids accident cover",
			},
			AccidentNotice: {
				title: "You are creating a standalone accident contract",
				description:
					"If your accident cover is arranged as a rider to a life insurance contract, it belongs there and you do not need to create it separately.",
			},
			Sections: {
				basicInfo: "Basic information",
				insurance: "Premium",
				additionalInfo: "Additional information",
			},
			Fields: {
				name: "Contract name",
				contractNumber: "Contract number",
				producer: "Provider",
				product: "Product",
				insurance: "Annual premium",
				insurancePeriod: "Periodic premium",
				paymentFrequency: "Payment frequency",
				signedAt: "Signed at",
				startAt: "Start date",
				endAt: "End date",
			},
		},
		AddLeasing: {
			title: "Leasing",
			Sections: {
				basicInfo: "Basic information",
				values: "Values",
				dates: "Dates",
				note: "Note",
			},
			Fields: {
				name: "Contract name",
				contractNumber: "Contract number",
				producer: "Provider",
				product: "Product name",
				productCategory: "Product category",
				signedAt: "Signed at",
				startAt: "Start date",
				endAt: "End date",
				originalObligationAmount: "Obligation amount",
				installment: "Installment",
				interestRate: "Interest rate",
				paymentFrequency: "Payment frequency",
				note: "Note",
			},
		},
		AddBusinessLoans: {
			title: "Business loan",
			Sections: {
				basicInfo: "Basic information",
				values: "Values",
				dates: "Dates",
				note: "Note",
			},
			Fields: {
				name: "Contract name",
				contractNumber: "Contract number",
				producer: "Provider",
				product: "Product name",
				signedAt: "Signed at",
				startAt: "Start date",
				endAt: "End date",
				originalObligationAmount: "Loan amount",
				installment: "Installment",
				interestRate: "Interest rate",
				fixationEndDate: "End of fixation",
				paymentFrequency: "Payment frequency",
				note: "Note",
			},
		},
		AddCryptocurrency: {
			title: "Cryptocurrencies",
			Placeholders: {
				name: "e.g. Exchange account",
			},
		},
		AddServices: {
			title: "Service",
			successTitle: "Done",
			successHeadline: "Added to portfolio!",
			successDescription: "The item is immediately visible in your portfolio.",
			successHint:
				"Your advisor has been notified and can fill in the missing details.",
			successAddAnother: "Add another",
			successBackToPortfolio: "Back to portfolio",
			submit: "Add to portfolio",
			Sections: {
				basicInfo: "Basic information",
				values: "Contract parameters",
				additionalInfo: "Additional information",
				note: "Note",
				showParameters: "Show parameters",
				hideParameters: "Hide parameters",
			},
			Fields: {
				name: "Contract name",
				producer: "Provider",
				product: "Product name",
				contractNumber: "Contract number",
				tariff: "Tariff",
				commodityService: "Service type",
				unitOfMeasurement: "Unit of measurement",
				pricePerUnit: "Price per unit",
				pricePerUnitWithUnit: "Price per {{unit}}",
				fixedPriceByFrequency: "Fixed price",
				paymentFrequency: "Payment frequency",
				depositAmount: "Deposit amount",
				signedAt: "Signed at",
				startAt: "Start date",
				endAt: "End date",
				note: "Note",
			},
			Placeholders: {
				unitOfMeasurement: "e.g. kWh",
				contractName: "e.g. Electricity flat Prague",
			},
			Validations: {
				maxLength: "Maximum length is {{max}} characters.",
				positiveNumber: "Value must be a positive number.",
				maxValue: "Value is too high.",
			},
		},
		Detail: {
			notAvailable: "Portfolio is no longer available",
		},
	},
	PortfolioDetail: {
		Pin: {
			alreadyPinned: "Already pinned",
			unpin: "Unpin",
			selectEmpty: "You have nothing else you could pin here yet.",
			selectEmptyHint: "Add a new item with the plus in the portfolio header.",
			selectEmptyAddItem: "Add item to portfolio",
			Kind: {
				document: {
					chooseTitle: "Documents",
					chooseIntro:
						"Attach a document you already have in Documents, or upload a new one. Unpinning detaches the document from this item, but it stays in your Documents.",
					existingTitle: "Existing document",
					existingSubtitle: "Pick from Documents",
					newTitle: "New document",
					newSubtitle: "Upload and link",
					selectTitle: "Select a document",
				},
				contract: {
					chooseTitle: "Pin a contract",
					chooseIntro:
						"Link this item with another contract in your portfolio.",
					existingTitle: "Existing contract",
					existingSubtitle: "Pick from portfolio",
					selectTitle: "Select a contract",
				},
				property: {
					chooseTitle: "Pin a property",
					chooseIntro: "Link this item with a property in your portfolio.",
					existingTitle: "Existing property",
					existingSubtitle: "Pick from portfolio",
					selectTitle: "Select a property",
				},
				service: {
					chooseTitle: "Pin a service",
					chooseIntro: "Link this item with a service in your portfolio.",
					existingTitle: "Existing service",
					existingSubtitle: "Pick from portfolio",
					selectTitle: "Select a service",
				},
			},
		},
		InvestmentValue: {
			updateTitle: "Update value",
			newValue: "New value",
			invested: "Total invested",
			date: "As of",
			previousValue: "Previous value:",
			placeholder: "Enter amount",
			confirm: "Save",
			backToFund: "Back to fund detail",
			savedToast: "Value updated",
			positiveValue: "Enter a value greater than zero.",
		},
		ChartTimeFrames: {
			threeMonths: "3M",
			sixMonths: "6M",
			oneYear: "1Y",
			threeYears: "3Y",
			fiveYears: "5Y",
			all: "All",
		},
		SharedLabels: {
			name: "Name",
			contractNumber: "Contract number",
			producent: "Provider",
			product: "Product",
			signDate: "Signing date",
			beginDate: "Date of beginning",
			endDate: "Date of ending",
			untilAge: "until {{count}} years",
			Frequency: {
				[ClientPortfolioPaymentFrequencyEnum.Monthly]: "Monthly",
				[ClientPortfolioPaymentFrequencyEnum.Single]: "Single",
				[ClientPortfolioPaymentFrequencyEnum.Yearly]: "Yearly",
				[ClientPortfolioPaymentFrequencyEnum.Semiannually]: "Semi-annually",
				[ClientPortfolioPaymentFrequencyEnum.Quarterly]: "Quarterly",
				[ClientPortfolioPaymentFrequencyEnum.None]: "None",
			},
			investedAll: "Total invested",
			recurringPayment: "Regular payment",
			currentValue: "Current value",
			targetAmount: "Target amount",
			paidAll: "Whole payment",
			empty: "No content",
			emptySubtitle:
				"This page has no data, please fill out the portfolio form in myDOCK.",
			Currency: {
				[CurrencyEnum.CZK]: "Kč",
				[CurrencyEnum.EUR]: "€",
				[CurrencyEnum.USD]: "$",
				[CurrencyEnum.GBP]: "£",
				[CurrencyEnum.JPY]: "¥",
				[CurrencyEnum.AUD]: "A$",
				[CurrencyEnum.SGD]: "S$",
				[CurrencyEnum.CHF]: "CHF",
				[CurrencyEnum.HUF]: "Ft",
				[CurrencyEnum.PLN]: "zł",
				[CurrencyEnum.NOK]: "kr",
				[CurrencyEnum.RON]: "lei",
				[CurrencyEnum.HKD]: "HK$",
				[CurrencyEnum.DKK]: "kr",
				[CurrencyEnum.CAD]: "C$",
				[CurrencyEnum.CNY]: "¥",
				[CurrencyEnum.NZD]: "NZ$",
				[CurrencyEnum.SEK]: "kr",
				[CurrencyEnum.TRY]: "₺",
				[CurrencyEnum.UNKNOWN]: "",
				Empty: "No currency",
			},
			CurrencyReverse: {
				[CurrencyEnum.CZK]: "CZK",
				[CurrencyEnum.EUR]: "EUR",
				[CurrencyEnum.USD]: "USD",
				[CurrencyEnum.GBP]: "GBP",
				[CurrencyEnum.JPY]: "JPY",
				[CurrencyEnum.AUD]: "AUD",
				[CurrencyEnum.SGD]: "SGD",
				[CurrencyEnum.CHF]: "CHF",
				[CurrencyEnum.HUF]: "HUF",
				[CurrencyEnum.PLN]: "PLN",
				[CurrencyEnum.NOK]: "NOK",
				[CurrencyEnum.RON]: "RON",
				[CurrencyEnum.HKD]: "HKD",
				[CurrencyEnum.DKK]: "DKK",
				[CurrencyEnum.CAD]: "CAD",
				[CurrencyEnum.CNY]: "CNY",
				[CurrencyEnum.NZD]: "NZD",
				[CurrencyEnum.SEK]: "SEK",
				[CurrencyEnum.TRY]: "TRY",
				[CurrencyEnum.UNKNOWN]: "",
				Empty: "No currency",
			},
		},
		LifeInsurance: {
			insuredAmount: "Insured",
			employersBonus: "Employers bonus",
			signDate: "Signing date",
			beginDate: "Date of beginning",
			endDate: "Date of ending",
			EnsuredPeople: {
				title: "Ensured people",
				Variant: {
					Constant: "Constant",
					Decreasing: "Decreasing",
					Annuities: "Annuities",
					Retroactively: "Retroactively",
					NonRetroactively: "Non-retroactively",
					Progression: "Progression",
					Empty: "Empty",
				},
			},
			Risk: {
				[ClientPortfolioInsuredPersonRiskEnum.Death]: "Death",
				[ClientPortfolioInsuredPersonRiskEnum.DeathByAccident]:
					"Death by accident",
				[ClientPortfolioInsuredPersonRiskEnum.Invalidity1]: "Invalidity 1",
				[ClientPortfolioInsuredPersonRiskEnum.Invalidity2]: "Invalidity 2",
				[ClientPortfolioInsuredPersonRiskEnum.Invalidity3]: "Invalidity 3",
				[ClientPortfolioInsuredPersonRiskEnum.Invalidity4]: "Invalidity 4",
				[ClientPortfolioInsuredPersonRiskEnum.SeriousIllness]:
					"Serious illness",
				[ClientPortfolioInsuredPersonRiskEnum.CancerFighting]:
					"For cancer fighting",
				[ClientPortfolioInsuredPersonRiskEnum.ForWoman]: "For woman",
				[ClientPortfolioInsuredPersonRiskEnum.DailyCompensationForInjury]:
					"Daily compensation for injury",
				[ClientPortfolioInsuredPersonRiskEnum.BodilyDamageInjury]:
					"Body damage injury",
				[ClientPortfolioInsuredPersonRiskEnum.IncapacityForWork]:
					"Incapacity for work",
				[ClientPortfolioInsuredPersonRiskEnum.Hospitalization]:
					"Hospitalization",
				[ClientPortfolioInsuredPersonRiskEnum.PermanentConsequencesOfInjuryFrom05Percent]:
					"Permanent consequences of injury from 0.5%",
				[ClientPortfolioInsuredPersonRiskEnum.PermanentConsequencesOfInjuryFrom10Percent]:
					"Permanent consequences of injury from 10%",
				[ClientPortfolioInsuredPersonRiskEnum.LoanPackage]: "Loan package",
			},
			InvestmentTool: {
				title: "Investment tool",
				IdentificationType: {
					[ClientPortfolioToolIdentificationTypeEnum.Isin]: "ISIN",
					[ClientPortfolioToolIdentificationTypeEnum.Other]: "Identification",
					[ClientPortfolioToolIdentificationTypeEnum.Crypto]: "Crypto",
					[ClientPortfolioToolIdentificationTypeEnum.BankProduct]:
						"Bank Product",
					[ClientPortfolioToolIdentificationTypeEnum.Null]: "Null",
					[ClientPortfolioToolIdentificationTypeEnum.None]: "None",
				},
				type: "Tool type",
				Type: {
					[ClientPortfolioToolTypeEnum.Fund]: "Funds",
					[ClientPortfolioToolTypeEnum.Bond]: "Bonds",
					[ClientPortfolioToolTypeEnum.Other]: "Other",
					[ClientPortfolioToolTypeEnum.Commodity]: "Commodities",
					[ClientPortfolioToolTypeEnum.Crypto]: "Cryptocurrencies",
					[ClientPortfolioToolTypeEnum.BankProduct]: "Bank Products",
					[ClientPortfolioToolTypeEnum.Share]: "Shares",
				},
			},
		},
		NonLifeInsurance: {
			name: "Name",
			contractNumber: "Contract number",
			producent: "Provider",
			product: "Product",
			insured: "Insured",
			insurancePeriod: "Term insurance",
			AdditionalInfo: {
				signDate: "Signing date",
				beginDate: "Date of beginning",
				endDate: "Date of ending",
			},
		},
		PensionProduct: {
			participantContribution: "Participant contribution",
			employerContribution: "Employer contribution",
			toAge: "To client's age",
		},
		BuildingSavings: {
			savings: "Savings",
			appreciation: "Appreciation rate",
			stateContribution: "Receives state contribution",
			note: "Note",
		},
		LoanForBusiness: {
			endOfFixation: "End of fixation",
		},
		Loan: {
			coDebtors: "Co-debtors",
			amount: "Amount",
			matury: "Maturity",
			interestRate: "Interest rate",
			installment: "Installment",
			Frequency: {
				Years: "{{count}} year",
				Years_one: "{{count}} year",
				Years_few: "{{count}} years",
				Years_other: "{{count}} years",
				Months: "{{count}} month",
				Months_one: "{{count}} month",
				Months_few: "{{count}} months",
				Months_other: "{{count}} months",
			},
		},
		Services: {
			name: "Name",
			contractNumber: "Contract number",
			producent: "Provider",
			product: "Product",
			insured: "Insured",
			cardLabel: "Contract parameters",
			advancePayment: "Advance payment",
			tariff: "Tariff",
			commodity: "Commodity",
			pricePerUnit: "Price for {{unit}}",
			pricePerUnitWithoutUnit: "Price per unit",
			fixedPrice: "Fixed price",
			CommodityService: {
				[ClientPortfolioDataCommodityServiceEnum.Electricity]: "Electricity",
				[ClientPortfolioDataCommodityServiceEnum.Gas]: "Gas",
				[ClientPortfolioDataCommodityServiceEnum.Water]: "Water",
				[ClientPortfolioDataCommodityServiceEnum.Internet]: "Internet",
				[ClientPortfolioDataCommodityServiceEnum.Television]: "Television",
				[ClientPortfolioDataCommodityServiceEnum.CellPhoneTarrif]:
					"Mobile tariff",
				[ClientPortfolioDataCommodityServiceEnum.Other]: "Other",
				[ClientPortfolioDataCommodityServiceEnum.None]: "None",
			},
			AdditionalInfo: {
				signDate: "Signing date",
				beginDate: "Date of beginning",
				endDate: "Date of ending",
				depositAmount: "Amount of the deposit",
			},
		},
		PaidAdvisory: {
			fixedPrice: "Fixed price",
		},
		ToolHistory: {
			title: "Instruction history",
			active: "Active",
			nonActive: "Non-active",
			button: "Actions history",
			purchase: "Purchase",
			ActionType: {
				[ClientPortfolioInvestmentTypeEnum.Buy]: "Buying",
				[ClientPortfolioInvestmentTypeEnum.Sell]: "Selling",
				[ClientPortfolioInvestmentTypeEnum.Empty]: "",
			},
			ActionTypeBankProduct: {
				[ClientPortfolioInvestmentTypeEnum.Buy]: "Deposit",
				[ClientPortfolioInvestmentTypeEnum.Sell]: "Withdrawal",
				[ClientPortfolioInvestmentTypeEnum.Empty]: "",
			},
			Fund: {
				type: "Instruction type",
				startDate: "Start date of instruction",
				endDate: "End date of instruction",
				amount: "Amount",
				frequency: "Frequency",
				spread: "Spread",
				transactionType: "Transaction type",
				disposition: "More disposition",
				entryTransaction: "Entry transaction",
				entryTransactionAmount: "Transaction amount",
				entryTransactionProcantage: "Charged on first payments",
				noFee: "No fee",
				applying: "Applying",
				Applying: {
					[ClientPortfolioInvestmentEnterFeeApplyingEnum.ChargedFeeFromInvestment]:
						"Charged from investment",
					[ClientPortfolioInvestmentEnterFeeApplyingEnum.PaidExtra]:
						"Paid extra",
				},
				Frequency: {
					[ClientPortfolioInvestmentFrequencyEnum.Monthly]: "Monthly",
					[ClientPortfolioInvestmentFrequencyEnum.EveryTwoMonths]:
						"Every two months",
					[ClientPortfolioInvestmentFrequencyEnum.Quarter]: "Quarterly",
					[ClientPortfolioInvestmentFrequencyEnum.SemiYearly]: "Semi-annually",
					[ClientPortfolioInvestmentFrequencyEnum.Yearly]: "Yearly",
					[ClientPortfolioInvestmentFrequencyEnum.Once]: "Once",
					[ClientPortfolioInvestmentFrequencyEnum.None]: "None",
				},
				TransactionType: {
					[ClientPortfolioInvestmentTransactionEnum.Trade]: "Trade",
					[ClientPortfolioInvestmentTransactionEnum.FundTransfer]:
						"Fund transfer",
				},
				EntryTransaction: {
					[ClientPortfolioInvestmentEnterFeeTypeEnum.Once]: "Once",
					[ClientPortfolioInvestmentEnterFeeTypeEnum.FromFirstPayments]:
						"From first payments",
					[ClientPortfolioInvestmentEnterFeeTypeEnum.FromEveryPayment]:
						"From every payment",
				},
				EntryTransactionAmount: {
					[ClientPortfolioInvestmentEnterFeeAmountTypeEnum.Amount]: "Kč",
					[ClientPortfolioInvestmentEnterFeeAmountTypeEnum.Percent]: "%",
				},
			},
			Bond: {
				issueDate: "Issue date",
				maturityDate: "Maturity date",
				value: "Value",
				amount: "Number of units",
				interestRate: "Interest rate/coupon",
				frequency: "Payment frequency",
				yield: "Yield p.a.",
				tax: "Taxation",
				fee: "Fee according to frequency",
				InterestType: {
					[ClientBondInterestTypeEnum.Fixed]: "Fixed interest",
					[ClientBondInterestTypeEnum.Variable]: "Variable interest",
					[ClientBondInterestTypeEnum.Nullable]: "Null coupon",
				},
			},
			Cryptocurrency: {
				title: "Cryptocurrency detail",
				name: "Cryptocurrency",
				holdingType: "Holding type",
				amount: "Amount",
				HoldingType: {
					[ClientPortfolioInstructionHoldingTypeEnum.InAdministration]:
						"In administration",
					[ClientPortfolioInstructionHoldingTypeEnum.OwnWallet]:
						"In own wallet",
				},
				HoldingTypeShort: {
					[ClientPortfolioInstructionHoldingTypeEnum.InAdministration]: "A",
					[ClientPortfolioInstructionHoldingTypeEnum.OwnWallet]: "W",
				},
			},
			Commodity: {
				title: "Commodities detail",
				name: "Commodity",
			},
			BankProducts: {
				title: "Account detail",
				name: "Account name",
				currentValue: "Current value",
				evaluation: "Evaluation",
			},
		},
		Investment: {
			InvestmentTool: {
				title: "Detail of investment",
			},
		},
		Property: {
			TypeOfConstruction: "Type of construction",
			PropertyCategoryValue: {
				[ClientPropertyCategoryEnum.Apartment]: "Apartment",
				[ClientPropertyCategoryEnum.OnKey]: "OnKey",
				[ClientPropertyCategoryEnum.LowEnergy]: "LowEnergy",
				[ClientPropertyCategoryEnum.Family]: "Family",
				[ClientPropertyCategoryEnum.Villa]: "Villa",
				[ClientPropertyCategoryEnum.Garage]: "Garage",
				[ClientPropertyCategoryEnum.HistoricalObject]: "HistoricalObject",
				[ClientPropertyCategoryEnum.Cottage]: "Cottage",
				[ClientPropertyCategoryEnum.CivicAmenityBuilding]:
					"CivicAmenityBuilding",
				[ClientPropertyCategoryEnum.Pond]: "Pond",
				[ClientPropertyCategoryEnum.Farmstead]: "Farmstead",
				[ClientPropertyCategoryEnum.OtherBuliding]: "OtherBuliding",
				[ClientPropertyCategoryEnum.Livving]: "Livving",
				[ClientPropertyCategoryEnum.CommercialBuilding]: "CommercialBuilding",
				[ClientPropertyCategoryEnum.Garden]: "Garden",
				[ClientPropertyCategoryEnum.PermanentGrassGrowth]:
					"PermanentGrassGrowth",
				[ClientPropertyCategoryEnum.Forest]: "Forest",
				[ClientPropertyCategoryEnum.AgriculturalLand]: "AgriculturalLand",
				[ClientPropertyCategoryEnum.OtherLand_]: "OtherLand_",
				[ClientPropertyCategoryEnum.None]: "None",
			},
			PropertyType: {
				[ClientPropertyTypeEnum.Art]: "Art",
				[ClientPropertyTypeEnum.AutoMoto]: "AutoMoto",
				[ClientPropertyTypeEnum.Commodity]: "Commodity",
				[ClientPropertyTypeEnum.Cottage]: "Other property",
				[ClientPropertyTypeEnum.Flat]: "Flat",
				[ClientPropertyTypeEnum.House]: "House",
				[ClientPropertyTypeEnum.Land]: "Land",
			},
			PropertyGroup: {
				[ClientPropertyTypeEnum.Art]: "Art",
				[ClientPropertyTypeEnum.AutoMoto]: "AutoMoto",
				[ClientPropertyTypeEnum.Commodity]: "Commodity",
				[ClientPropertyTypeEnum.Cottage]: "Real estate",
				[ClientPropertyTypeEnum.Flat]: "Real estate",
				[ClientPropertyTypeEnum.House]: "Real estate",
				[ClientPropertyTypeEnum.Land]: "Real estate",
			},
			PropertyTypeRow: "Property type",
			LandTypeEnum: {
				[ClientPropertyCategoryEnum.Apartment]: "Apartment",
				[ClientPropertyCategoryEnum.OnKey]: "OnKey",
				[ClientPropertyCategoryEnum.LowEnergy]: "LowEnergy",
				[ClientPropertyCategoryEnum.Family]: "Family",
				[ClientPropertyCategoryEnum.Villa]: "Villa",
				[ClientPropertyCategoryEnum.Garage]: "Garage",
				[ClientPropertyCategoryEnum.HistoricalObject]: "HistoricalObject",
				[ClientPropertyCategoryEnum.Cottage]: "Cottage",
				[ClientPropertyCategoryEnum.CivicAmenityBuilding]:
					"CivicAmenityBuilding",
				[ClientPropertyCategoryEnum.Pond]: "Pond",
				[ClientPropertyCategoryEnum.Farmstead]: "Farmstead",
				[ClientPropertyCategoryEnum.OtherBuliding]: "OtherBuliding",
				[ClientPropertyCategoryEnum.Livving]: "Livving",
				[ClientPropertyCategoryEnum.CommercialBuilding]: "CommercialBuilding",
				[ClientPropertyCategoryEnum.Garden]: "Garden",
				[ClientPropertyCategoryEnum.PermanentGrassGrowth]:
					"PermanentGrassGrowth",
				[ClientPropertyCategoryEnum.Forest]: "Forest",
				[ClientPropertyCategoryEnum.AgriculturalLand]: "AgriculturalLand",
				[ClientPropertyCategoryEnum.OtherLand_]: "OtherLand_",
				[ClientPropertyCategoryEnum.None]: "None",
			},
			SecondaryBuildingArea: "Secondary building area",
			MarketPriceHistory: "Property market price history",
			ValueSourceEstimate: "{{source}} estimate as of {{date}}",
			LandType: "Land type",
			StreetAndNumber: "Street and number",
			Municipality: "Municipality",
			ZipCode: "ZIP Code",
			EstimatedPrice: "Estimated price",
			ReproductionPrice: "Reproduction price",
			MarketPrice: "Market price",
			CurrentValue: "Current value",
			ForInvestments: "Investment",
			ParcelNumber: "Parcel number",
			ShowParameters: "Show {{noun}} parameters",
			HideParameters: "Hide {{noun}} parameters",
			ParametersNounRealEstate: "property",
			ParametersNounVehicle: "vehicle",
			ValueDrawer: {
				updateTitle: "Update value",
				fillTitle: "Add value",
				confirm: "Save",
				savedToast: "The value has been saved.",
				positiveValue: "Enter a value greater than zero.",
				placeholder: "Enter an amount",
			},
			InteriorPrice: "Interior price",
			LandPrice: "Land price",
			LandArea: "Land area",
			Ownership: "Ownership",
			OwnershipValue: {
				[ClientPropertyOwnershipEnum.Cooperative]: "Cooperative",
				[ClientPropertyOwnershipEnum.DEPRECATED_EMPTY]: "Empty",
				[ClientPropertyOwnershipEnum.National]: "National",
				[ClientPropertyOwnershipEnum.NationalMunicipal]: "National/Municipal",
				[ClientPropertyOwnershipEnum.None]: "None",
				[ClientPropertyOwnershipEnum.Personal]: "Personal",
				[ClientPropertyOwnershipEnum.Other]: "Other",
			},
			Disposition: "Disposition",
			DispositionValue: {
				[ClientPropertyBuildingDispositionEnum.None]: "None",
				[ClientPropertyBuildingDispositionEnum.OnePlusKK]: "1+kk",
				[ClientPropertyBuildingDispositionEnum.OnePlusOne]: "1+1",
				[ClientPropertyBuildingDispositionEnum.TwoPlusKK]: "2+kk",
				[ClientPropertyBuildingDispositionEnum.TwoPlusOne]: "2+1",
				[ClientPropertyBuildingDispositionEnum.ThreePlusKK]: "3+kk",
				[ClientPropertyBuildingDispositionEnum.ThreePlusOne]: "3+1",
				[ClientPropertyBuildingDispositionEnum.FourPlusKK]: "4+kk",
				[ClientPropertyBuildingDispositionEnum.FourPlusOne]: "4+1",
				[ClientPropertyBuildingDispositionEnum.FivePlusKK]: "5+kk",
				[ClientPropertyBuildingDispositionEnum.FivePlusOne]: "5+1",
				[ClientPropertyBuildingDispositionEnum.SixPlusKK]: "6+kk",
				[ClientPropertyBuildingDispositionEnum.SixPlusOne]: "6+1",
				[ClientPropertyBuildingDispositionEnum.SevenPlusKK]: "7+kk",
				[ClientPropertyBuildingDispositionEnum.SevenPlusOne]: "7+1",
				[ClientPropertyBuildingDispositionEnum.Atypical]: "Atypical",
				[ClientPropertyBuildingDispositionEnum.StudioApartment]:
					"Studio apartment",
				[ClientPropertyBuildingDispositionEnum.Custom]: "Custom",
			},
			Floors: "Floors",
			NumberOfFloors: "Number of Floors",
			NumberOfAboveGroundFloors: "Number of above-ground floors",
			BuildingMaterial: "Building material",
			BuildingMaterialValue: {
				[ClientPropertyBuildingMaterialEnum.Brick]: "Brick",
				[ClientPropertyBuildingMaterialEnum.Custom]: "Custom",
				[ClientPropertyBuildingMaterialEnum.Empty]: "Empty",
				[ClientPropertyBuildingMaterialEnum.Mixed]: "Mixed",
				[ClientPropertyBuildingMaterialEnum.None]: "None",
				[ClientPropertyBuildingMaterialEnum.Panel]: "Panel",
				[ClientPropertyBuildingMaterialEnum.ReinforcedConcrete]:
					"Reinforced concrete",
				[ClientPropertyBuildingMaterialEnum.Skeletal]: "Skeletal",
				[ClientPropertyBuildingMaterialEnum.Stone]: "Stone",
				[ClientPropertyBuildingMaterialEnum.Wood]: "Wood",
			},
			FloorArea: "Total floor area",
			UsableArea: "Usable area",
			Renovation: "Renovation",
			RenovationValue: {
				[ClientPropertyRenovationEnum.Continuous]: "Continuous",
				[ClientPropertyRenovationEnum.InProgress]: "In progress",
				[ClientPropertyRenovationEnum.None]: "None",
				[ClientPropertyRenovationEnum.Null]: "Null",
				[ClientPropertyRenovationEnum.Total]: "Total",
				[ClientPropertyRenovationEnum.DEPRECATED_EMPTY]: "Empty",
			},
			YearOfRenovation: "Year of renovation",
			Properties: "Extras",
			ApartmentNumber: "Apartment number",
			HouseholdArea: "Household area",
			ItemTitle: "Item title",
			AdditionalDescription: "Additional description",
			Description: "Description",
			ArtName: "Name / description",
			PurchaseDate: "Purchase date",
			Value: "Value",
			CarPropertyType: "Car type",
			CarPropertyTypeValue: {
				[ClientPropertyVehicleTypeEnum.Bus]: "Bus",
				[ClientPropertyVehicleTypeEnum.CarUpTo3_5t]: "Passenger vehicle",
				[ClientPropertyVehicleTypeEnum.Motorcycle]: "Motorbike/ATV",
				[ClientPropertyVehicleTypeEnum.Motorhome]: "Camper",
				[ClientPropertyVehicleTypeEnum.None]: "None",
				[ClientPropertyVehicleTypeEnum.OtherElectro]: "Other electro",
				[ClientPropertyVehicleTypeEnum.OtherVehicles]: "Other",
				[ClientPropertyVehicleTypeEnum.Tractor]: "Tractor",
				[ClientPropertyVehicleTypeEnum.Trailer]: "Trailer",
				[ClientPropertyVehicleTypeEnum.Truck]: "Truck",
				[ClientPropertyVehicleTypeEnum.UtilityCar]: "Commercial vehicle",
				[ClientPropertyVehicleTypeEnum.WorkMachine]: "Working machine",
			},
			CarBodyType: "Car body type",
			CarBodyTypeValue: {
				[ClientCarBodyTypeEnum.Cabriolet]: "Cabriolet",
				[ClientCarBodyTypeEnum.Combi]: "Combi",
				[ClientCarBodyTypeEnum.Coupe]: "Coupe",
				[ClientCarBodyTypeEnum.Hatchback]: "Hatchback",
				[ClientCarBodyTypeEnum.Limousine]: "Limousine",
				[ClientCarBodyTypeEnum.Mpv]: "MPV",
				[ClientCarBodyTypeEnum.None]: "None",
				[ClientCarBodyTypeEnum.OffRoad]: "Off-Road",
				[ClientCarBodyTypeEnum.Other]: "Other",
				[ClientCarBodyTypeEnum.PickUp]: "Pick-Up",
				[ClientCarBodyTypeEnum.Roadster]: "Roadster",
				[ClientCarBodyTypeEnum.Sedan]: "Sedan",
				[ClientCarBodyTypeEnum.Suv]: "SUV",
				[ClientCarBodyTypeEnum.Van]: "Van",
				[ClientCarBodyTypeEnum.liftback]: "Liftback",
				[ClientCarBodyTypeEnum.DEPRECATED_EMPTY]: "Empty",
			},
			CarVehicleType: "Car vehicle type",
			CarVehicleTypeValue: {
				[ClientPropertyCarVehicleTypeEnum.ElectricScooter]: "Electric scooter",
				[ClientPropertyCarVehicleTypeEnum.Empty]: "Empty",
				[ClientPropertyCarVehicleTypeEnum.GardenTractor]: "Garden tractor",
				[ClientPropertyCarVehicleTypeEnum.GolfCart]: "Golf cart",
				[ClientPropertyCarVehicleTypeEnum.Kart]: "Kart",
				[ClientPropertyCarVehicleTypeEnum.None]: "None",
				[ClientPropertyCarVehicleTypeEnum.Other]: "Other",
				[ClientPropertyCarVehicleTypeEnum.Segway]: "Segway",
			},
			CarLicensePlate: "License plate",
			CarBrand: "Brand",
			CarModel: "Model",
			CarVin: "VIN",
			CarPurchaseDate: "Car purchase date",
			CarRegistration: "Car registration",
			CreatedAt: "Created at",
			CarPutIntoService: "Car put into service",
			CarPower: "Car power",
			CarVolume: "Car volume",
			CarWeight: "Car weight",
			CarSeatCount: "Car seat count",
			CarFuelType: "Car fuel type",
			CarFuelTypeValue: {
				[ClientCarFuelEnum.Cng]: "CNG",
				[ClientCarFuelEnum.Diesel]: "Diesel",
				[ClientCarFuelEnum.DieselCng]: "Diesel + CNG",
				[ClientCarFuelEnum.DieselLpg]: "Diesel + LPG",
				[ClientCarFuelEnum.Electricity]: "Electricity",
				[ClientCarFuelEnum.HybridDiesel]: "Hybrid Diesel",
				[ClientCarFuelEnum.HybridPetrol]: "Hybrid Petrol",
				[ClientCarFuelEnum.Lpg]: "LPG",
				[ClientCarFuelEnum.None]: "None",
				[ClientCarFuelEnum.Other]: "Other",
				[ClientCarFuelEnum.Petrol]: "Petrol",
				[ClientCarFuelEnum.PetrolCng]: "Petrol + CNG",
				[ClientCarFuelEnum.PetrolLpg]: "Petrol + LPG",
			},
			CarSecurity: "Car security",
			CarAlarmSecurity: "Car alarm security",
			CarActiveTrackingSecurity: "Car active tracking security",
			CarMechanicalSecurity: "Car mechanical security",
			CarImmobilizerSecurity: "Car immobilizer security",
			CarGlassEtchingSecurity: "Car glass etching security",
			CarPassiveTrackingSecurity: "Car passive tracking security",
			CarSpecialEquipment: "Car special equipment",
			CarSpecialEquipment44: "Car special equipment 44",
			CarSpecialEquipmentAudiovisualSystem: "Car audiovisual system",
			CarSpecialEquipmentLeatherUpholstery: "Car leather upholstery",
			CarSpecialEquipmentPanoramicRoof: "Car panoramic roof",
			CarSpecialEquipmentParkingAssistant: "Car parking assistant",
			CarValue: "Car value",
			VehicleParameters: "Vehicle parameters",
			Category: "Category",
			CategoryValue: {
				[ClientPropertyCategoryEnum.Apartment]: "Apartment",
				[ClientPropertyCategoryEnum.OnKey]: "On key",
				[ClientPropertyCategoryEnum.LowEnergy]: "Low energy",
				[ClientPropertyCategoryEnum.Family]: "Family",
				[ClientPropertyCategoryEnum.Villa]: "Villa",
				[ClientPropertyCategoryEnum.Garage]: "Garage",
				[ClientPropertyCategoryEnum.HistoricalObject]: "Historical object",
				[ClientPropertyCategoryEnum.Cottage]: "Cottage",
				[ClientPropertyCategoryEnum.CivicAmenityBuilding]:
					"Civic amenity building",
				[ClientPropertyCategoryEnum.Pond]: "Pond",
				[ClientPropertyCategoryEnum.Farmstead]: "Farmstead",
				[ClientPropertyCategoryEnum.OtherBuliding]: "Other building",
				[ClientPropertyCategoryEnum.Livving]: "Living",
				[ClientPropertyCategoryEnum.CommercialBuilding]: "Commercial building",
				[ClientPropertyCategoryEnum.Garden]: "Garden",
				[ClientPropertyCategoryEnum.PermanentGrassGrowth]:
					"Permanent grass growth",
				[ClientPropertyCategoryEnum.Forest]: "Forest",
				[ClientPropertyCategoryEnum.AgriculturalLand]: "Agricultural land",
				[ClientPropertyCategoryEnum.OtherLand_]: "Other land",
				[ClientPropertyCategoryEnum.None]: "None",
			},
			Terrace: "Terrace",
			Loggia: "Loggia",
			Balcony: "Balcony",
			Garage: "Garage",
			Cellar: "Cellar",
			Elevator: "Elevator",
			BarrierFree: "Barrier-free",
			SwimmingPool: "Swimming pool",
			ParkingSpace: "Parking space",
			Furnished: "Furnished",
			YearOfCompletion: "Year of completion",
		},
	},
	Finances: {
		title: "Financial balance",
		Intro: {
			title: "Fill out your financial balance",
			description: "You don't have any income or expenses yet",
			button: "Fill out",
			result: "Result",
			surplusTitle: "Use the surplus",
			surplusDescription:
				"On average monthly, you have <balance>{{balance}}</balance>. This surplus can be further evaluated. Contact your advisor and manage your finances effectively.",
			deficitTitle: "Opportunities to balance the budget",
			deficitDescription:
				"Your expenses exceed your income by <balance>{{balance}}</balance> per month. Contact your advisor and find a way to improve the balance together.",
			contactAdvisor: "Contact advisor",
		},
		Income: {
			titleTentatively: "Monthly income",
			descriptionTentative:
				"To tell you what your financial balance is, we need to know your income.",
			titleExact: "Enter in detail",
			descriptionExact: {
				intro:
					"If you have several different types of income and want to have them all clearly listed, you can use the detailed entry option.",
				categories: [
					{
						title: "Main regular income, e.g.:",
						items: ["salary", "wage", "business income"],
					},
					{
						title: "Additional income, e.g.:",
						items: [
							"part-time jobs",
							"honorariums",
							"contract income",
							"other side activities",
						],
					},
					{
						title: "Passive income not requiring active work, e.g.:",
						items: [
							"rent",
							"dividends",
							"investment returns",
							"licensing fees",
						],
					},
					{
						title: "Income from the state, e.g.:",
						items: [
							"parental allowance",
							"pension",
							"unemployment benefits",
							"other social benefits",
						],
					},
					{
						title:
							"Other income that doesn't fit into the above categories, e.g.:",
						items: ["winnings", "gifts", "income from abroad"],
					},
				],
			},
			subtitleExact: "What to count?",
			readMore: "Read more",
			readLess: "Hide",
			addIncome: "Add income",
			continue: "Continue to expenses",
		},
		IncomeModal: {
			modalTitle: "Income",
			name: "Custom name",
			amount: "Amount",
		},
		Expenses: {
			titleTentatively: "Monthly expenses",
			descriptionTentative:
				"We also need to know your expenses so that we can calculate how much money you have left each month.",
			titleExact: "Enter in detail",
			subtitleExact: "What to count?",
			descriptionExact:
				"If you have different types of expenses and want to have them all neatly filled in, you can use their detailed entry.",
			whatToCountDescription:
				"Main regular expenses – e.g., household costs, housing, transportation, or insurance. Savings – amounts that you regularly set aside (savings, investments, supplementary pension insurance). Tax expenses – e.g., income tax advances or other mandatory payments. Other expenses – all other regular or significant expenses that do not fall into the above categories.",
			minimumValueDescription: "min. amount",
			Types: {
				[ClientFinancialPlanExpenseTypeEnum.Household]: {
					title: "Household",
					description:
						"Enter regular expenses associated with the normal running of your household – e.g., groceries, drugstore items, services (internet), and other necessities.",
				},
				[ClientFinancialPlanExpenseTypeEnum.Housing]: {
					title: "Housing",
					description:
						"Enter all housing-related expenses – e.g., mortgage, rent, utilities, housing-related services, energy, repair fund, maintenance.",
				},
				[ClientFinancialPlanExpenseTypeEnum.Insurance]: {
					title: "Insurance",
					description:
						"Enter all your payments for life, property, and other insurance, or for health and social insurance (self-employed persons).",
				},
				[ClientFinancialPlanExpenseTypeEnum.Saving]: {
					title: "Savings",
					description:
						"Enter the amounts you regularly set aside for savings accounts, investments, supplementary pension insurance, or other forms of savings.",
				},
				[ClientFinancialPlanExpenseTypeEnum.Taxes]: {
					title: "Taxes",
					description:
						"Enter expenses related to tax payments – e.g., income tax advances, tax advisor fees, etc.",
				},
				[ClientFinancialPlanExpenseTypeEnum.Traffic]: {
					title: "Transport",
					description:
						"Enter your transportation costs – including car payments, fuel, public transportation, vehicle insurance, and maintenance.",
				},
				[ClientFinancialPlanExpenseTypeEnum.Other]: {
					title: "Other",
					description:
						"Enter any other regular or significant expenses that do not fall into the above categories.",
				},
			},
			Names: {
				[ClientFinancialPlanExpenseNameEnum.Rent]: "Rent",
				[ClientFinancialPlanExpenseNameEnum.EnergyBills]: "Energy",
				[ClientFinancialPlanExpenseNameEnum.Fees]: "Fees",
				[ClientFinancialPlanExpenseNameEnum.OtherHousing]: "Other housing",
				[ClientFinancialPlanExpenseNameEnum.Leasing]: "Leasing",
				[ClientFinancialPlanExpenseNameEnum.TollFees]: "Toll fees",
				[ClientFinancialPlanExpenseNameEnum.CarInsurance]: "Car insurance",
				[ClientFinancialPlanExpenseNameEnum.FuelExpenses]: "Fuel expenses",
				[ClientFinancialPlanExpenseNameEnum.Maintenance]: "Maintenance",
				[ClientFinancialPlanExpenseNameEnum.Fares]: "Fares",
				[ClientFinancialPlanExpenseNameEnum.BuildingSavings]:
					"Building savings",
				[ClientFinancialPlanExpenseNameEnum.PensionProducts]:
					"Pension products",
				[ClientFinancialPlanExpenseNameEnum.Investments]: "Investments",
				[ClientFinancialPlanExpenseNameEnum.LifeInsurance]: "Life insurance",
				[ClientFinancialPlanExpenseNameEnum.HouseholdInsurance]:
					"Household insurance",
				[ClientFinancialPlanExpenseNameEnum.Responsibility]: "Responsibility",
				[ClientFinancialPlanExpenseNameEnum.FoodExpenses]: "Food expenses",
				[ClientFinancialPlanExpenseNameEnum.PocketMoney]: "Pocket money",
				[ClientFinancialPlanExpenseNameEnum.Hairdressing]: "Hairdressing",
				[ClientFinancialPlanExpenseNameEnum.Clothing]: "Clothing",
				[ClientFinancialPlanExpenseNameEnum.Sport]: "Sport",
				[ClientFinancialPlanExpenseNameEnum.Culture]: "Culture",
				[ClientFinancialPlanExpenseNameEnum.Restaurants]: "Restaurants",
				[ClientFinancialPlanExpenseNameEnum.Pets]: "Pets",
				[ClientFinancialPlanExpenseNameEnum.Subscription]: "Subscription",
				[ClientFinancialPlanExpenseNameEnum.Books]: "Books",
				[ClientFinancialPlanExpenseNameEnum.MobilePhone]: "Mobile phone",
				[ClientFinancialPlanExpenseNameEnum.TuitionFees]: "Tuition fees",
				[ClientFinancialPlanExpenseNameEnum.Gifts]: "Gifts",
				[ClientFinancialPlanExpenseNameEnum.Vacation]: "Vacation",
				[ClientFinancialPlanExpenseNameEnum.CharityContributions]:
					"Charity contributions",
				[ClientFinancialPlanExpenseNameEnum.RegularCleaning]:
					"Regular cleaning",
				[ClientFinancialPlanExpenseNameEnum.AlimonyPayments]:
					"Alimony payments",
				[ClientFinancialPlanExpenseNameEnum.Babysitter]: "Babysitter",
				[ClientFinancialPlanExpenseNameEnum.Other]: "Other",
				[ClientFinancialPlanExpenseNameEnum.Loans]: "Loans",
				[ClientFinancialPlanExpenseNameEnum.Cigarettes]: "Cigarettes",
				[ClientFinancialPlanExpenseNameEnum.SocialTax]: "Social tax",
				[ClientFinancialPlanExpenseNameEnum.IncomeTax]: "Income tax",
				[ClientFinancialPlanExpenseNameEnum.PropertyTax]: "Property tax",
				[ClientFinancialPlanExpenseNameEnum.Total]: "Total",
			},
		},
		ExpensePicker: {
			title: "Expenses",
			value: "Monthly payment",
			minimumValue: "Minimum value",
			description:
				"Enter the minimum amount you will always have to spend on this expense, even if your income decreases.",
		},
		Overview: {
			title: "Result",
			description: "The result of your financial balance is:",
			balancePossitive: "Use balance",
			balancePossitiveDescription:
				"Your current surplus of <balance>{{balance}}</balance> Kč per month opens up opportunities to work more efficiently with your finances – for example, through savings, investments, or optimizing your financial plan.",
			balanceNegative: "Opportunities to balance the budget",
			balanceNegativeDescription:
				"Your expenses exceed your income by <balance>{{balance}}</balance> per month. We recommend consulting with your advisor – they can help, for example, with optimizing expenses, creating a savings plan, or finding other solutions.",
			contactAdvisor: "Contact advisor",
			finishButton: "Finish",
			checkButton: "Check my income and expenses",
		},
		Offline: {
			editError: "Financial balance cannot be edited in offline mode.",
			saveError: "Saving failed. Check your internet connection and try again.",
		},
	},
	Notifications: {
		title: "Notifications",
		readAll: "Mark all as read",
		term: "Term",
		Tabs: {
			unread: "Unread",
			all: "All",
		},
		Date: {
			today: "Today",
			daysAgo: "{{count}} days ago",
			daysAgo_one: "Yesterday",
			daysAgo_other: "{{count}} days ago",
			lastWeek: "Last week",
		},
		noContent: "There are currently no unread notifications",
	},
	NotificationCenter: {
		Title: "Notification center",
		Tabs: {
			Upcoming: "Upcoming",
			Recent: "Recent",
		},
		Filter: {
			Button: "Filter",
		},
		Empty: {
			Upcoming: "No upcoming events or activities",
			Recent: "No past events or activities yet",
		},
		EmptyState: {
			Upcoming: {
				title: "No upcoming events",
				description: "There are no important dates coming up.",
			},
			Recent: {
				title: "No recent events",
				description: "Nothing important has happened in the last 14 days.",
			},
		},
		UpcomingGroups: {
			ResolvingNow: "Now resolving",
			In7Days: "In 7 days",
			In14Days: "In 14 days",
			In30Days: "In 30 days",
			In3Months: "In 3 months",
			In6Months: "In 6 months",
			In12Months: "In 12 months",
		},
		RecentGroups: {
			Today: "Today",
			ThisWeek: "This week",
			LastWeek: "Last week",
			WeeksAgo: "{{count}} weeks ago",
			WeeksAgo_few: "{{count}} weeks ago",
			WeeksAgo_other: "{{count}} weeks ago",
		},
		Common: {
			Retry: "Retry",
			LoadingError: "Loading failed",
			LoadingMore: "Loading more...",
		},
		FilterScreen: {
			Title: "Filter",
			HorizonSectionTitle: "Horizon",
			TypeSectionTitle: "Type",
			Apply: "Apply filters",
			ResetAriaLabel: "Reset filter",
			Horizon: {
				Month: "30 days",
				HalfYear: "6 months",
				Year: "12 months",
			},
			Type: {
				All: "All",
				Activities: "Activities",
				Events: "Events",
				Notifications: "Notifications",
			},
		},
	},
	DataCard: {
		BasicInfo: {
			title: "Basic informations",
			additionalInfo: "Additional informations",
		},
		Documents: {
			title: "Documents",
		},
		RelatedContracts: {
			title: "Related contracts",
		},
		RelatedProperties: {
			title: "Related properties",
		},
		RelatedServices: {
			title: "Related services",
		},
		RelatedCard: {
			document: {
				title: "Documents",
				empty:
					"Attach a contract, confirmation or receipt to keep them at hand with this item.",
				pin: "Add document",
				edit: "Edit documents",
			},
			contract: {
				title: "Related contracts",
				empty: "Link contracts that belong together and see them in one place.",
				pin: "Pin a contract",
				edit: "Edit links",
			},
			property: {
				title: "Related properties",
				empty: "Link the property tied to this contract.",
				pin: "Pin a property",
				edit: "Edit links",
			},
			service: {
				title: "Related services",
				empty: "Link services attached to this contract.",
				pin: "Pin a service",
				edit: "Edit links",
			},
		},
		relatedPinToast: "Item pinned",
		documentPinUnavailableToast: "Adding documents is coming soon.",
		relatedUnpinToast: "Item unpinned",
		PropertyValue: {
			title: "Property value",
		},
		PropertyParameters: {
			title: "Property parameters",
		},
		Value: {
			title: "Value",
		},
	},
	ExpenseCard: {
		addExpense: "Add expense",
	},
	Form: {
		Validations: {
			required: "This field is required",
		},
	},
	DocumentDetail: {
		noDocuments: "No documents found",
	},
	BarChart: {
		income: "Income",
		expenses: "Expenses",
	},
	Inspiration: {
		disclaimer:
			"Values are indicative and may differ. This is not investment advice. For an accurate calculation, contact your financial advisor.",
		softWarning: "Your value is unusual, please check that it is correct.",
		OwnHome: {
			category: "Housing",
			title: "Your own roof over your head",
			subtitle: "Stop paying someone else's mortgage",
			introPrefix:
				"Paying rent and wondering if owning your home is within reach? We'll show you what you can afford, ",
			introBold: "just a few numbers",
			introSuffix: ".",
			exampleTitle: "How it works, example",
			exampleBody:
				"Rent of <b>{{rent}}/month</b> = mortgage payment on a flat worth <blue>~{{property}}</blue>.",
			rentCardLabel: "After 30 years renting",
			ownCardLabel: "After 30 years owning",
			exampleHint: "We'll calculate it for your situation · takes 30 s",
			cta: "Calculate for me",
			RatesUnavailable: {
				title: "Rates could not be loaded",
				description:
					"Without current partner bank rates we cannot calculate the estimate. Please try again in a moment.",
			},
			Sheet: {
				title: "Quick estimate",
				description:
					"Three values are enough to estimate what you qualify for. Nothing is saved, just an indicative estimate.",
				rentLabel: "Monthly rent",
				incomeLabel: "Net monthly income",
				savingsLabel: "Savings (optional)",
				savingsHint: "Helps estimate how much you'll need at the start.",
				submit: "Calculate for me",
				saveToBilance: "Save to financial balance",
			},
			Result: {
				comparisonTitle: "Renting vs. owning",
				comparisonText:
					"You pay <b>{{rent}}</b> a month in rent. With the same monthly payment you could buy a flat at today's price <blue>{{range}}</blue>.",
				comparisonTextWithSavings:
					"You pay <b>{{rent}}</b> a month in rent. With the same monthly payment and savings of <b>{{savings}}</b> you could buy a flat at today's price <blue>{{range}}</blue>.",
				comparisonSourceNote: "Based on the values you entered.",
				comparisonRentTag: "Rent",
				comparisonTotalLabel: "You'll pay in total",
				comparisonEndLabel: "What you have at the end",
				comparisonRentEnd: "Nothing",
				comparisonOwnTag: "Own",
				comparisonPropertyLabel: "Flat value in {{years}} years",
				comparisonHint:
					"The range comes from our partner bank rates ({{rates}}%) and property price growth of 2 to 3% per year.",
				comparisonHintExpenses:
					" For a more accurate estimate, add your monthly expenses.",
				affordabilityTitle: "What you can afford",
				edit: "Edit",
				affordabilityApprox: "Indicative estimate",
				affordabilityExact: "Exact estimate",
				maxMortgageLabel: "Maximum mortgage amount",
				rowMaxPayment: "Max. monthly payment",
				rowIncome: "Net monthly income",
				rowExpenses: "Monthly expenses",
				rowCashflow: "Amount left each month",
				affordabilityFootnoteApprox:
					"Estimate based on income. It will be more accurate with expenses.",
				affordabilityFootnoteExact: "Estimate based on the values you entered.",
				nudgeExpensesTitle: "Refine your estimate",
				nudgeExpensesText:
					"Add your monthly expenses for a more realistic estimate based on what you have left each month.",
				downPaymentTitle: "Down payment, how much you need",
				downPaymentHeading: "You need ~{{gap}} more from your own funds",
				downPaymentText:
					"Recommended own funds of 10% = {{downPayment}}. You have {{savings}}. Saving {{monthly}} a month you will be ready in ~{{years}} years.",
				timelineTitle: "How it works",
				createGoal: 'Create goal "Own home"',
				createGoalSheet: {
					title: 'We\'ll create the goal "Own home"',
					description:
						"Based on your inputs we've pre-filled the goal's starting values. You can adjust everything and set the deadline in the form.",
					priceLabel: "Property price",
					priceNote:
						"Upper estimate, mortgage at {{rate}}% + your savings, rounded to hundred-thousands",
					loanLabel: "Mortgage",
					loanNote: "Banks usually want you to pay ~20% of the price yourself",
					savingsLabel: "Your savings",
					savingsNote: "Entered in the quick estimate",
					targetLabel: "Left to save for own funds",
					targetNote: "Target amount for regular saving until the deadline",
					footnote:
						"Values are indicative. The bank's actual offer may differ based on your income, the chosen property and other factors.",
					continue: "Continue to the form",
				},
				contactAdvisor: "Discuss with an advisor",
				advisorMessage:
					"Hello, I am interested in owning my own home and mortgage financing options. Can we connect about it?",
				Timeline: {
					prep: {
						title: "Preparation",
						duration: "2-4 weeks",
						desc: "How much the bank will lend you, document preparation",
					},
					search: {
						title: "Property search",
						duration: "individual",
						desc: "Viewings, reservation agreement",
					},
					mortgage: {
						title: "Mortgage application",
						duration: "2-4 weeks",
						desc: "Advisor compares bank offers",
					},
					approval: {
						title: "Approval and signing",
						duration: "1-2 weeks",
						desc: "Signing contracts, drawdown",
					},
					moving: {
						title: "Moving",
						duration: "",
						desc: "Ownership transfer, insurance",
					},
				},
			},
			ExpensesSheet: {
				title: "Your monthly expenses",
				description:
					"Expenses tell us how much of your income is actually left. The result will be more accurate.",
				expensesLabel: "Total monthly expenses",
				submit: "Calculate more accurately",
				saveToBilance: "Save to financial balance",
			},
			AffordabilitySheet: {
				title: "Edit details",
				description: "Changing any value recalculates the result.",
				incomeLabel: "Net monthly income",
				expensesLabel: "Monthly expenses",
				submit: "Recalculate",
			},
		},
		EarlyFreedom: {
			category: "Security",
			title: "Financial independence",
			subtitle: "When you stop working, your assets earn for you",
			cta: "Calculate for me",
			intro:
				"What if your assets supported you instead of your job? We will calculate <b>how much you need to save and when you can get there</b>, just a few numbers.",
			exampleTitle: "How it works, example",
			exampleBody:
				"You want to spend <b>{{spend}}</b> a month. You already get <b>{{rent}}</b> from rent. That leaves <orange>{{gap}}</orange> to cover, for which you need to save about <blue>{{capital}}</blue>.",
			exampleTodayLabel: "From assets today",
			exampleNeedLabel: "You need",
			exampleHint: "We will calculate it for your situation in 30 s",
			targetTitle: "What kind of freedom you want",
			targetDescription:
				"How much would you want per month once you no longer have to work?",
			targetSliderLabel: "Monthly expenses",
			passiveTitle: "Income outside work",
			edit: "Edit",
			passiveFullLabel: "Rental income",
			perMonth: "/mo",
			passiveLiteDescription:
				"Do you already have rental income? It lowers the amount you need to cover.",
			passiveNudgeTitle: "Income outside work",
			passiveNudgeText: "Tell us how much rent you get each month. Takes 20 s.",
			capitalTitle: "How much you need to save",
			rowExpenses: "Monthly expenses",
			rowPassive: "From assets today",
			rowGap: "Left to cover",
			capitalLabel: "You need to save",
			capitalNote: "at a safe withdrawal rate of 4% per year",
			capitalDisclaimer:
				"Rent already earns for you, so we do not count it in this number. We will work out which investments get you to this amount in the next step.",
			paceTitle: "Your pace to independence",
			paceSliderLabel: "Monthly investment",
			scenarioConservative: "Conservative",
			scenarioBalanced: "Balanced",
			scenarioDynamic: "Dynamic",
			perYear: "p.a.",
			paceNow: "Now",
			paceNever: "—",
			year: "year",
			year_one: "year",
			year_few: "years",
			year_other: "years",
			ageLabel: "you will be {{age}}",
			paceNote: "Your advisor will fine-tune the exact strategy.",
			phasesTitle: "How it works",
			readyTitleAlmost: "You are practically financially independent",
			readyTitleFull: "You are financially independent",
			readyKicker: "Financially independent",
			ReadySteps: {
				drawdown: {
					title: "Start drawdown",
					subtitle: "Monthly budget from returns",
				},
				portfolio: {
					title: "More conservative portfolio",
					subtitle: "Stability for independence",
				},
			},
			readyDescAlmost:
				"Your passive income almost covers your planned expenses. Let us finish it with an advisor.",
			readyDescFull:
				"Your passive income already covers your planned expenses. Go through it with your advisor to keep it that way.",
			contactAdvisor: "Contact advisor",
			createGoal: 'Create goal "Financial independence"',
			discussAdvisor: "Discuss with advisor",
			advisorMessage:
				"Hi, I would like to discuss the “Financial independence” inspiration. Target passive income: {{targetIncome}}, required capital: {{targetCapital}}, monthly investment: {{monthlySaving}}, return scenario: {{scenario}} ({{rate}}% p.a.).",
			advisorMessageReady:
				"Hi, I would like to discuss the “Financial independence” inspiration. Planned expenses: {{targetExpenses}}, passive income today: {{passiveIncome}} ({{coverage}}% covered). Can we finish it off?",
			QuickSheet: {
				title: "Calculate for me",
				description:
					"Let us find out how much you need to save. One number is enough. Nothing is saved.",
				sliderLabel: "Monthly expenses",
				hint: "How much you would spend if you did not have to work.",
				submit: "Let us do it",
			},
			IncomeSheet: {
				title: "Income outside work",
				description:
					"We only add up what actually lands in your account from rent.",
				sliderLabel: "Rental income",
				hint: "After costs (tax, maintenance, vacancy). You can take it from your balance.",
				submit: "Done",
			},
			Anchors: {
				modest: { label: "Minimal", description: "Home, food, small hobbies" },
				comfort: {
					label: "Comfortable",
					description: "Travel and a good life",
				},
				luxury: { label: "Custom", description: "Set it your own way" },
			},
			Phases: {
				diversify: {
					title: "Spread your sources",
					duration: "ongoing",
					desc: "Stocks and funds, rental property, bonds and pension savings",
				},
				build: {
					title: "Letting money grow",
					duration: "most of the time",
					desc: "More stocks (70-90%), returns reinvested right away",
				},
				approaching: {
					title: "Approaching the goal",
					duration: "the last 5 years",
					desc: "You gradually move into safer investments and keep part of your money as a reserve for bad years",
				},
				living: {
					title: "You live off it",
					duration: "",
					desc: "A safer portfolio and a cash reserve. When markets struggle, you take less; in good years you can take more.",
				},
			},
		},
	},
	Goals: {
		List: {
			title: "My Goals",
			activeGoals: "Active Goals",
			completed: "Completed",
			ctaTitle: "Have another dream?",
			ctaTitleCompleted: "What is your next plan?",
			ctaDescription: "Tell us what you want and see the path to get there.",
			ctaButton: "I want a new goal",
			Status: {
				onTrack: "On track",
				warning: "Needs attention",
				offTrack: "Off track",
				unplanned: "Not planned",
			},
			DueIn: {
				Years: "{{count}} year",
				Years_one: "{{count}} year",
				Years_few: "{{count}} years",
				Years_other: "{{count}} years",
				Months: "{{count}} month",
				Months_one: "{{count}} month",
				Months_few: "{{count}} months",
				Months_other: "{{count}} months",
			},
		},
		EmptyState: {
			title: "Where are you heading?",
			description:
				"Tell us where you're heading and your agent will find the way with you.",
			addGoalButton: "I want to set a goal",
			getAdviceButton: "Get advice",
			advisorMessage:
				"Hi, I would like to set a financial goal. Could you advise me how to start?",
			examplesTitle: "Get inspired",
			addLabel: "Add",
			Examples: {
				Retirement: {
					name: "Financial freedom",
					reason: "When you no longer have to go to work.",
				},
				Housing: {
					name: "Keys to your own place",
					reason: "Your own roof over your head.",
				},
				Vacation: {
					name: "Dream vacation",
					reason: "What you've been putting off for years.",
				},
			},
		},
		Detail: {
			title: "My goal",
			dueInYear: "In {{dueIn}} ({{year}})",
			completedInYear: "Completed {{year}}",
			Blocked: {
				cta: "Message advisor",
				advisorMessage:
					"I want to change the goal '{{goal}}'. Requested change: ",
				Locked: {
					title: "Action restricted",
					heading: "Your advisor manages this action",
					description:
						"Changes to this goal are managed by your advisor. If you want a change, arrange it via chat.",
				},
				Completed: {
					title: "A completed goal cannot be edited",
					description:
						"The goal is marked as completed, so it is read-only. If you want to edit it, move it back to active first.",
				},
			},
			Actions: {
				ariaLabel: "Goal actions",
				complete: "Mark as completed",
				reactivate: "Move back to active",
				delete: "Delete goal",
				notFoundToast: "This goal no longer exists",
				Complete: {
					title: "Mark the goal as completed?",
					description:
						"The goal {{name}} will move to completed goals and become read-only. Its contracts stay pinned. You can move it back to active at any time.",
					confirm: "Mark as completed",
					cancel: "Cancel",
					successToast: "Goal marked as completed",
				},
				Reactivate: {
					title: "Move the goal back to active?",
					description:
						"The goal {{name}} will move back to active goals. Its status will be recalculated from the target date and pinned contracts.",
					confirm: "Move back to active",
					cancel: "Cancel",
					successToast: "Goal is back among active goals",
				},
			},
			Delete: {
				title: "Delete goal?",
				descriptionPrefix: "Are you sure you want to delete the goal ",
				descriptionSuffix: "? This action cannot be undone.",
				confirm: "Delete goal",
				cancel: "Keep",
				successToast: "Goal deleted",
			},
			WelcomePin: {
				title: "Goal created!",
				WithOwnFunds: {
					description:
						"You said you have {{amount}} CZK set aside. Where do you keep this money?",
					cta: "Assign the money set aside",
				},
				WithoutOwnFunds: {
					description:
						"Do you have a contract in your portfolio that this goal could be filled with?",
					cta: "Yes, pick a contract",
				},
				dismiss: "Not yet",
			},
			Status: {
				onTrack: "On track",
				warning: "Needs attention",
				offTrack: "Off track",
				completed: "Completed",
				unplanned: "Not planned",
			},
			OwnFunds: {
				title: "Set aside ({{amount}} CZK)",
				emptyTitle: "Tell us where it sits",
				emptyDescription:
					"Pin a savings account, fund, or wherever you keep this money.",
				pinContract: "Pin a contract",
			},
			OwnFundsCallout: {
				title: "Do you already have something set aside?",
				descriptionWithLoan:
					"Add those funds to the goal and lower the amount you need to save.",
				descriptionRent:
					"Connect your existing savings, pension, reserve or investments.",
				descriptionSavings:
					"Add what you already have and shorten your way to the goal.",
				Drawer: {
					title: "How much do you have set aside?",
					description:
						"Enter the amount you already have set aside. We will add it and lower the amount you need to save.",
					label: "Set aside",
					placeholder: "E.g. 500,000",
					submit: "Save",
				},
			},
			Solution: {
				title: "Remaining to save ({{amount}} CZK)",
				emptyTitle: "This goal has no contracts yet",
				emptyDescription:
					"Pin an existing contract from your portfolio or add a future one.",
				valueLabel: "{{amount}} CZK",
				allocationLabel: "{{percent}} % of contract",
				monthlyLabel: "{{amount}}/month",
				expectedInYear: "→ {{amount}} in {{year}}",
				futureBadge: "Future",
				loanLabel: "Loan {{amount}}",
				lumpSumLabel: "{{amount}} lump sum",
				pinContract: "Pin contract",
				Actions: {
					detail: "Contract detail",
					updateValue: "Update value",
					editAllocation: "Edit allocation",
					editAllocationDescription: "Currently {{percent}} % of contract",
					unpin: "Unpin from goal",
				},
				Unpin: {
					title: "Unpin contract",
					description:
						"{{name}} stays in your portfolio; it just stops counting toward this goal.",
					confirm: "Unpin contract",
					cancel: "Keep",
				},
				Allocation: {
					sliderLabel: "Allocation to this goal",
					usedOnOther:
						"{{usedPercent}} % of this contract is allocated to other goals. You can assign up to {{maxPercent}} % to this goal.",
					previewTitle: "Counted toward this goal",
					valueLabel: "Value",
					outOf: "of {{amount}} CZK",
					submit: "Save allocation",
				},
				Pin: {
					chooseTitle: "Pin contract",
					chooseDescription:
						"Link the goal to a contract and see how close you are.",
					existingTitle: "Existing contract",
					existingDescription: "Pick from portfolio",
					futureTitle: "Future contract",
					futureDescription: "Your advisor will help arrange it",
					Existing: {
						title: "Select contract",
						noEligible:
							"Your portfolio has no contracts suitable for this goal.",
						hint: "Showing only contracts that can fund the goal: savings, investments, pension. Each row shows how much of its capacity is still free.",
						addNew: "Add a new contract",
						fullyAllocated: "Available capacity {{percent}} % (min {{min}} %)",
						attachedGoals: "Held by: {{goals}}",
						attachedGoal: "{{name}} ({{percent}} %)",
						alreadyPinned: "Already pinned ({{percent}} %), edit",
						available: "Available {{percent}} %",
					},
					Future: {
						title: "Future contract",
						editTitle: "Edit future contract",
						nameLabel: "Contract name",
						namePlaceholder: "E.g. Pension savings",
						typeLabel: "Type",
						Types: {
							Investment: "Investments",
							Mortgage: "Mortgage",
							Loan: "Loan",
						},
						loanAmountLabel: "How much I'll borrow",
						loanAmountPlaceholder: "E.g. 2 000 000",
						lumpSumLabel: "Lump sum",
						lumpSumPlaceholder: "E.g. 100 000",
						monthlyLabel: "Monthly amount",
						monthlyPlaceholder: "E.g. 5 000",
						startMonthLabel: "Start date",
						noteLabel: "Note",
						notePlaceholder: "E.g. After refinancing is done",
						save: "Add future contract",
						saveEdit: "Save changes",
						edit: "Edit",
						delete: "Delete",
						DeleteConfirm: {
							title: "Delete planned contract",
							description:
								"The planned contract {{name}} will be removed from this goal.",
							confirm: "Delete contract",
							cancel: "Cancel",
						},
					},
				},
			},
			LoanCalculator: {
				titleMortgage: "Planned mortgage ({{amount}} CZK)",
				titleLoan: "Planned loan ({{amount}} CZK)",
				descriptionMortgage:
					"Once you save up the down payment, realization begins and the mortgage covers the rest.",
				descriptionLoan:
					"Once you save up the down payment, realization begins and the loan covers the rest.",
				rateLabel: "Interest rate",
				rateValue: "{{rate}} % p.a.",
				rateMin: "2 %",
				rateMax: "8 %",
				termLabel: "Repayment period",
				termValue: "{{count}} years",
				termValue_one: "{{count}} year",
				termValue_few: "{{count}} years",
				termValue_other: "{{count}} years",
				monthlyPayment: "Monthly payment",
				monthlyMeta: "at {{rate}} % p.a. · {{term}}",
				totalPaid: "Total paid",
				amountValue: "{{amount}} CZK",
				interest: "Of which interest",
				interestValue: "+{{amount}} CZK",
				disclaimer:
					"Indicative calculation. Your advisor will prepare a specific offer based on the bank and your creditworthiness.",
				showDetail: "Show detail",
				hideDetail: "Hide detail",
			},
			RentPlan: {
				title: "Rent plan",
				phaseAccumulation: "Accumulation",
				phaseDrawdown: "Drawdown",
				accumYearsLabel: "{{count}} years of saving",
				drawdownPerpetualLabel: "{{amount}}/mo forever",
				drawdownToZeroLabel: "{{years}} years · {{amount}}/mo",
				durationYears: "{{count}} years",
				accumPhaseTitle: "Now, {{year}}",
				accumPhaseDescription: "You save {{amount}} before the rent starts.",
				drawPhaseTitlePerpetual: "{{startYear}}, forever",
				drawPhaseTitleToZero: "{{startYear}}, {{endYear}}",
				drawPhaseDescriptionPerpetual:
					"You draw {{amount}} monthly from the yield. The principal stays as an inheritance.",
				drawPhaseDescriptionToZero:
					"You draw {{amount}} monthly, gradually depleting the principal to zero.",
				disclaimer: "Calculated at a real return of 4% p.a. after inflation.",
			},
			ExtraContribution: {
				title: "What if I added more?",
				placeholder: "Move the slider",
				talkToAdvisor: "Talk to your advisor",
				advisorMessage:
					'Hi, I am interested in increasing the monthly contribution to my goal "{{goal}}" by {{extra}}. Can we discuss it?',
				otherOptions: "Other options",
				Earlier: {
					label: "Finish sooner",
					subtitle: "{{saved}} earlier",
					today: "Today",
					earlier: "earlier",
					years: "{{count}} years",
					years_one: "{{count}} year",
					years_few: "{{count}} years",
					years_other: "{{count}} years",
					months: "{{count}} months",
					months_one: "{{count}} month",
					months_few: "{{count}} months",
					months_other: "{{count}} months",
					and: "and",
					none: "0 months",
				},
				More: {
					label: "Save more",
					subtitle: "+{{amount}} by completion",
					originalGoal: "Original goal",
					withIncrease: "With increase",
					extraToTarget: "+{{amount}} extra by completion",
				},
				Split: {
					label: "Split smartly",
					subtitle: "{{here}} here, {{there}} elsewhere",
					thisGoal: "This goal",
					redirectSurplus: "Redirect surplus",
					efficient: "You're investing more efficiently into two goals",
					noSplit: "Add more to see a split suggestion",
				},
			},
			Summary: {
				mustHave: "I need to have",
				alreadySetAside: "({{amount}} already set aside)",
				thenLoan: "Then a loan",
				thenMortgage: "Then a mortgage",
				setAside: "Set aside",
				PriceLabels: {
					housing: "Property value",
					car: "Car price",
					total: "Total price",
					rent: "I will need in total",
				},
				Rent: {
					label: "Rent",
					perpetual: "perpetual",
					toZero: "until depleted",
					monthly: "{{amount}}/mo",
					duration: "for {{count}} years",
					duration_one: "for {{count}} year",
					duration_few: "for {{count}} years",
					duration_other: "for {{count}} years",
				},
			},
			Composition: {
				title: "Goal composition",
				ownFunds: "Own funds",
				loan: "Loan",
				savings: "Savings and investments",
				remaining: "Remaining to cover",
				coveredLabel: "covered",
			},
			Projection: {
				tileTitle: "Expected savings in {{year}}",
				missing: "Missing: {{amount}} CZK",
				warningWithCurrent:
					"You're close to the plan. Just add {{amount}} CZK/month and you'll make it by {{year}}.",
				warningNoCurrent:
					"With {{amount}} CZK/month extra, you'll make it by {{year}}.",
				offTrackWithCurrent:
					"Current {{current}} CZK/month isn't enough. You need at least {{needed}} CZK. Your advisor can help find a way.",
				offTrackNoCurrent:
					"This goal has no savings yet. Start with {{amount}} CZK/month and you'll get rolling.",
				rentSource:
					"Based on rent of {{amount}} CZK/{{frequency}} over {{years}} years",
				Frequency: {
					monthly: "month",
					yearly: "year",
				},
			},
		},
		Amount: {
			millions: "M",
			thousands: "K",
			currencySign: "CZK",
		},
		Form: {
			titleCreate: "New goal",
			titleEdit: "Edit goal",
			sectionCategory: "Category",
			sectionAbout: "About the goal",
			sectionAmount: "How much will it cost?",
			nameLabel: "Goal name",
			namePlaceholder: "E.g. Wedding",
			nameMaxLength: "Maximum length is {{max}} characters.",
			dueDateLabel: "Target date",
			amountPlaceholder: "E.g. 500,000",
			Validations: {
				dueDateTooSoon: "The target date can be next month at the earliest",
				dueDateTooFar:
					"The target date can be at most {{years}} years from now",
				startDateTooSoon: "The contract can start this month at the earliest",
				startDateTooFar:
					"The contract can start at most {{years}} years from now",
				amountPositive: "The amount must be greater than 0",
				amountMax: "The amount can be at most {{max}} {{currency}}",
				rentYearsRange:
					"The drawdown period must be between {{min}} and {{max}} years",
			},
			Notices: {
				overpaidTitle: "Funds exceed the goal value",
				overpaidDescription:
					"You have {{excess}} more than the goal value. You can still save the goal.",
				statePensionCoversAllTitle: "The state pension covers the whole rent",
				statePensionCoversAllDescription:
					"The state pension of {{state}} covers the whole rent of {{desired}}, so you do not need to save anything. The goal cannot be saved. Lower the state pension or raise the requested rent.",
			},
			ConfirmEdit: {
				title: "Do you really want to change the goal?",
				description:
					"Your advisor is working on this goal. They will see the change.",
				confirm: "Yes, change it",
				cancel: "Cancel",
			},
			SectionTitles: {
				Default: "Price and financing",
				Housing: "Property price and financing",
				Car: "Car price and financing",
				Retirement: "How do you want to draw your rent?",
				Education: "How much do you want?",
				Vacation: "How much do you want?",
			},
			AmountLabels: {
				Default: "Total price",
				Housing: "Property price",
				Car: "Car price",
				Retirement: "How much I'll need",
				Education: "Target amount",
				Vacation: "Target amount",
			},
			AmountHints: {
				Retirement:
					"How much will you need in retirement? Try the rent calculator, or enter the amount directly.",
				Education:
					"Tuition, supplies, accommodation: add it all up. It doesn't need to be exact.",
			},
			OwnFundsLabels: {
				Default: "Set aside",
				Housing: "Set aside",
				Car: "Set aside",
				Education: "Set aside",
				Vacation: "Set aside",
			},
			LoanLabels: {
				Default: "I want a loan",
				Housing: "I want a mortgage",
				Car: "I want a loan",
			},
			LoanHints: {
				Default:
					"We'll handle the interest rate and payments in the goal detail.",
				Housing:
					"You'll set the mortgage rate, term, and monthly payment in the goal detail.",
				Car: "We'll handle the interest rate and payments in the goal detail.",
			},
			RemainingLabels: {
				Default: "I need to save",
				Saving: "I need to save",
			},
			MonthlyConversion: {
				kicker: "Today, just save",
				heroSuffix: "CZK monthly · 4% per year",
				fomo: "If you wait a year, it will be <bold>{{monthlyDelayed}} CZK a month</bold>, <bold>{{extra}} CZK</bold> more.",
				caption: "Add the goal and start filling it today.",
			},
			Realization: {
				defaultTitle: "You don't need to save anymore",
				caption:
					"Once the goal is created, we'll guide you through the next steps.",
				Variants: {
					Home: {
						kicker: "Ready to realize",
						description:
							"You have the funds for the down payment. Let's go straight to the mortgage and property search.",
						Steps: {
							preApproval: {
								title: "Mortgage pre-approval",
								subtitle: "Arrange with your agent",
							},
							search: {
								title: "Property search",
								subtitle: "Tips and comparisons in Reality Hub",
							},
							documents: {
								title: "Mortgage documents",
								subtitle: "Income, statements, identity",
							},
						},
					},
					Car: {
						kicker: "Ready to realize",
						description:
							"You have the funds for the down payment. Let's go straight to the loan and choosing a car.",
						Steps: {
							preApproval: {
								title: "Loan pre-approval",
								subtitle: "Loan or leasing with your agent",
							},
							pick: {
								title: "Choose a car",
								subtitle: "Models, equipment, financing",
							},
							documents: {
								title: "Loan documents",
								subtitle: "Income, statements, identity",
							},
						},
					},
					Retirement: {
						kicker: "Ready for retirement",
						description:
							"You're saved up. Let's start the retirement drawdown.",
						Steps: {
							start: {
								title: "Start drawdown",
								subtitle: "Date and amount with your agent",
							},
							strategy: {
								title: "Retirement strategy",
								subtitle: "More conservative portfolio for stability",
							},
						},
					},
					Education: {
						kicker: "Ready to realize",
						description: "You're saved up. Let's go straight to education.",
						Steps: {
							enroll: {
								title: "Enrollment and payment",
								subtitle: "Arrange the date and pay",
							},
							plan: {
								title: "Plan the studies",
								subtitle: "Start and schedule",
							},
						},
					},
					Custom: {
						kicker: "Ready to realize",
						description: "You're saved up. The goal is ready to realize.",
						Steps: {
							schedule: {
								title: "Set a date",
								subtitle: "When you'll start",
							},
							tasks: {
								title: "First steps",
								subtitle: "What needs to be done",
							},
						},
					},
					Vacation: {
						kicker: "Ready to travel",
						description: "You're saved up. Let's plan the trip.",
						Steps: {
							book: {
								title: "Transport & accommodation",
								subtitle: "Trip booking",
							},
							plan: {
								title: "Insurance & documents",
								subtitle: "Travel insurance, visas",
							},
						},
					},
					Rent: {
						kicker: "Ready to draw down",
						description: "You're saved up. Let's set up the rent payouts.",
						Steps: {
							start: {
								title: "Set up drawdown",
								subtitle: "When to start, how much monthly",
							},
							strategy: {
								title: "Investment strategy",
								subtitle: "Rebalancing for the drawdown phase",
							},
						},
					},
				},
			},
			Pension: {
				sectionTitle: "How do you want to draw your rent?",
				Drawdown: {
					toZeroLabel: "Until depleted",
					perpetualLabel: "Perpetual rent",
				},
				monthlyLabel: "Monthly amount",
				monthlyPlaceholder: "E.g. 30,000",
				statePensionLabel: "Of which state pension",
				statePensionPlaceholder: "E.g. 18,000",
				yearsLabel: "For",
				yearsPlaceholder: "E.g. 20",
				yearsSuffix: "years",
				privateMonthlyLabel: "From your own funds monthly",
				privateMonthlyBreakdown: "{{desired}} wanted − {{state}} state",
				totalLabelPerpetual: "You need to have (forever)",
				totalLabelToZero: "You need to have at start of drawdown",
				totalDescriptionPerpetual:
					"You draw only the real return, principal remains as inheritance. Calculated with 4% p.a. real return after inflation.",
				totalDescriptionToZero:
					"You'll deplete the principal over {{years}} years. Calculated with 4% p.a. real return after inflation.",
				ownFundsLabel: "Set aside",
				ownFundsPlaceholder: "E.g. 500,000",
			},
			submitCreate: "Add goal",
			submitEdit: "Save",
			cancel: "Cancel",
			Categories: {
				Housing: "Housing",
				Car: "Car",
				Retirement: "Retirement",
				Rent: "Rent",
				Education: "Education",
				Vacation: "Vacation",
				Custom: "Custom",
			},
			DefaultNames: {
				Housing: "Buying a home",
				Car: "New car",
				Retirement: "For retirement",
				Rent: "Passive rent",
				Education: "Kids' education",
				Vacation: "Dream vacation",
			},
		},
	},
	Reminders: {
		Section: {
			title: "Reminders",
			addButton: "Add reminder",
			emptyTitle: "No reminders yet",
		},
		Badge: {
			soon: "Soon",
			overdue: "Overdue",
			snoozed: "Snoozed",
			snoozedUntil: "Snoozed until {{date}}",
		},
		Row: {
			subtitle: "until {{date}} · {{recurrence}}",
		},
		Recurrence: {
			Once: "One-time",
			Yearly: "Every year",
			Every2Years: "Every 2 years",
			Every3Years: "Every 3 years",
			Every4Years: "Every 4 years",
		},
		Form: {
			addTitle: "Add reminder",
			editTitle: "Edit reminder",
			addCta: "Add reminder",
			editCta: "Save changes",
			nameLabel: "Reminder name",
			namePlaceholder: "E.g. Next payment",
			dateLabel: "Reminder date",
			dateRowLabel: "Date",
			datePlaceholder: "Select date",
			recurrenceLabel: "Recurrence",
			customTemplate: "Custom",
			defaultName: "Reminder",
			selectDate: "Select a date",
			selectedDate: "Selected: {{date}}",
			duplicateDate: "You already have a reminder on {{date}}.",
			openExisting: "Open it",
		},
		Validations: {
			nameRequired: "Enter a name",
			nameMax: "Maximum 50 characters",
			dateRequired: "Select a date",
			dateFuture: "Date must be in the future",
			dateAfterReminder: "Snooze date must be after the reminder date",
		},
		Actions: {
			resolve: "Mark as resolved",
			snooze: "Snooze",
			edit: "Edit",
			remove: "Remove",
			cancelSnooze: "Cancel snooze",
		},
		Snooze: {
			title: "Snooze reminder",
			tomorrow: "Tomorrow",
			in3Days: "In 3 days",
			in1Week: "In 1 week",
			in2Weeks: "In 2 weeks",
			customDate: "Custom date…",
			confirm: "Snooze",
			toast: "Reminder snoozed until {{date}}.",
		},
		Resolve: {
			nextScheduledToast: "Next {{name}} scheduled for {{date}}.",
		},
		Templates: {
			Vehicle: {
				stk: "Vehicle inspection",
				service: "Service",
				insurance: "Insurance",
				vignette: "Motorway vignette",
				emissions: "Emissions check",
			},
			RealEstate: {
				chimney: "Chimney inspection",
				gas: "Gas inspection",
				electrical: "Electrical inspection",
				fireExtinguisher: "Fire extinguisher",
				propertyTax: "Property tax",
			},
		},
	},
	Auth: {
		LoginForm: {
			offline: "You are working in offline mode.",
		},
	},
	VersionUpdate: {
		modalTitle: "New application version",
		modalContent:
			"A new application version was found. Do you want to restart the app?",
	},
	Standalone: {
		TabBar: {
			dashboard: "Home",
			portfolio: "Portfolio",
			dashboardDisabledTooltip:
				"The client has the reporting display turned off in myPLANN. The preview will be available once it is enabled.",
		},
	},
	EventAlert: {
		moreEvents: "More events ({{count}})",
		dismissAriaLabel: "Dismiss alert",
		dontNotify: "I don't want to be notified about this event",
		inDays: "in {{count}} days",
		inDays_one: "in {{count}} day",
		inDays_few: "in {{count}} days",
		inDays_other: "in {{count}} days",
		inDaysCapitalized: "In {{count}} days",
		inDaysCapitalized_one: "In {{count}} day",
		inDaysCapitalized_few: "In {{count}} days",
		inDaysCapitalized_other: "In {{count}} days",
		Actions: {
			contactAdvisor: "Contact advisor",
			compareRates: "Compare rates",
			renewInsurance: "Renew policy",
			recalculateInsurance: "Recalculate policy",
			arrangeNew: "Arrange new",
			checkConditions: "Check conditions",
		},
		ActionState: {
			sent: "Sent {{date}}",
			lastOpened: "Last opened {{date}}",
		},
		ContactDialog: {
			title: "Contact advisor",
			description:
				"We'll send your advisor a request to contact you about this contract and they will get back to you as soon as possible.",
			button: "I'm interested",
			successTitle: "Done",
			successDescription: "Your request has been sent to your advisor.",
		},
		Content: {
			[EventClassificationEnum.LifeInsuranceEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"{{daysCap}} your life insurance expires. Without renewal you lose your insurance protection. We recommend acting in advance and consulting renewal with your advisor.",
				textToday:
					"Your life insurance expires today. Without renewal your insurance protection ends. Contact your advisor as soon as possible.",
				textPast:
					"Your life insurance expired {{date}}. The insurance protection has ended. We recommend acting without delay.",
			},
			[EventClassificationEnum.LifeInsuranceAnniversary]: {
				titleFuture: "Policy anniversary {{days}}",
				titleToday: "Policy anniversary is today",
				titlePast: "Policy anniversary has passed",
				textFuture:
					"{{daysCap}} your life insurance anniversary occurs. It is a good time to check whether the terms still match your current life situation and needs.",
				textToday:
					"Your life insurance anniversary is today. Check that the terms and coverage are up to date.",
				textPast:
					"Your life insurance anniversary recently passed. Consider consulting the terms with your advisor.",
			},
			[EventClassificationEnum.HousingLoanEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"{{daysCap}} your mortgage contract ends. Discuss repayment, refinancing or a follow-up solution with your advisor.",
				textToday: "Your mortgage contract ends today. Contact your advisor.",
				textPast:
					"Your mortgage contract has ended. Contact your advisor for a follow-up solution.",
			},
			[EventClassificationEnum.HousingLoanFixationEnd]: {
				titleFuture: "Fixation ends {{days}}",
				titleToday: "Fixation expires today",
				titlePast: "Fixation has ended",
				textFuture:
					"{{daysCap}} your mortgage interest rate fixation ends. After this date the rate changes to the bank's current rate unless you choose a new fixation or refinance.",
				textToday:
					"Your mortgage interest rate fixation expires today. Unless you choose a new fixation or refinance, you move to the bank's current rate.",
				textPast:
					"The mortgage interest rate fixation has expired. You are now on the bank's current rate.",
			},
			[EventClassificationEnum.ConsumerLoanEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"{{daysCap}} your loan contract ends. Discuss repayment or a follow-up solution with your advisor.",
				textToday: "Your loan contract ends today. Contact your advisor.",
				textPast:
					"Your loan contract has ended. Contact your advisor for a follow-up solution.",
			},
			[EventClassificationEnum.LoanEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"{{daysCap}} your loan contract ends. Contact your advisor if you want to discuss next steps.",
				textToday: "Your loan contract ends today. Contact your advisor.",
				textPast:
					"Your loan contract has ended. Contact your advisor if you are looking into further financing.",
			},
			[EventClassificationEnum.ConsumerLoanFixationEnd]: {
				titleFuture: "Fixation ends {{days}}",
				titleToday: "Fixation expires today",
				titlePast: "Fixation has ended",
				textFuture:
					"{{daysCap}} the interest rate fixation of your loan expires. After this date the rate may change. We recommend consulting your advisor.",
				textToday:
					"The interest rate fixation of your loan expires today. Contact your advisor.",
				textPast:
					"The loan interest rate fixation has expired. Contact your advisor for new terms.",
			},
			[EventClassificationEnum.PensionEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"{{daysCap}} your pension contract expires. Contact your advisor for instructions on payout, transfer or reinvestment of the saved funds.",
				textToday:
					"Your pension contract expires today. Contact your advisor for instructions.",
				textPast:
					"Your pension contract has expired. Contact your advisor for payout or reinvestment.",
			},
			[EventClassificationEnum.BuildingSavingsEnd]: {
				titleFuture: "Building savings expire {{days}}",
				titleToday: "Building savings expire today",
				titlePast: "Building savings expired",
				textFuture:
					"{{daysCap}} your building savings expire. It is time to decide whether to withdraw the funds or move to the loan phase. We recommend acting in advance and consulting the next steps with your advisor.",
				textToday:
					"Your building savings expire today. Contact your advisor to withdraw the funds or switch to the loan phase.",
				textPast:
					"Your building savings have expired. Contact your advisor regarding the saved funds.",
			},
			[EventClassificationEnum.InvestmentEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"{{daysCap}} your investment contract expires. Contact your advisor for instructions on the next steps with your investment.",
				textToday:
					"Your investment contract expires today. Contact your advisor.",
				textPast:
					"Your investment contract has expired. Contact your advisor for instructions.",
			},
			[EventClassificationEnum.BankProductEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"{{daysCap}} your contract expires. Contact your advisor for renewal or arranging an alternative banking product.",
				textToday: "Your contract expires today. Contact your advisor.",
				textPast: "Your contract has expired. Contact your advisor.",
			},
			[EventClassificationEnum.BusinessLoanEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"{{daysCap}} your business loan contract ends. Contact your advisor if you want to discuss next steps or new financing.",
				textToday:
					"Your business loan contract ends today. Contact your advisor.",
				textPast:
					"Your business loan contract has ended. Contact your advisor if you are looking into further financing.",
			},
			[EventClassificationEnum.BusinessLoanFixationEnd]: {
				titleFuture: "Fixation ends {{days}}",
				titleToday: "Fixation expires today",
				titlePast: "Fixation has ended",
				textFuture:
					"{{daysCap}} the interest rate fixation of your business loan expires. We recommend consulting refinancing or a new fixation with your advisor in advance.",
				textToday:
					"The interest rate fixation of your business loan expires today. Contact your advisor.",
				textPast:
					"The business loan fixation has expired. Contact your advisor for new terms.",
			},
			[EventClassificationEnum.RealEstateEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"{{daysCap}} your real estate contract expires. Contact your advisor for renewal or arranging a new one.",
				textToday: "Your contract expires today. Contact your advisor.",
				textPast: "Your contract has expired. Contact your advisor.",
			},
			[EventClassificationEnum.CommodityEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"{{daysCap}} your commodity contract expires. Contact your advisor for instructions on the next steps.",
				textToday: "Your contract expires today. Contact your advisor.",
				textPast: "Your contract has expired. Contact your advisor.",
			},
			[EventClassificationEnum.ServiceEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"{{daysCap}} your service contract expires. Contact your advisor for renewal or arranging a new one.",
				textToday: "Your contract expires today. Contact your advisor.",
				textPast: "Your contract has expired. Contact your advisor.",
			},
			[EventClassificationEnum.ServiceAnniversary]: {
				titleFuture: "Contract anniversary {{days}}",
				titleToday: "Contract anniversary is today",
				titlePast: "Contract anniversary has passed",
				textFuture:
					"{{daysCap}} your contract anniversary occurs. It is a good time to review the terms and possibly renegotiate.",
				textToday:
					"Your contract anniversary is today. Consider consulting the terms with your advisor.",
				textPast:
					"Your contract anniversary recently passed. Consider consulting the terms.",
			},
			[EventClassificationEnum.CarInsuranceEnd]: {
				titleFuture: "Policy ends {{days}}",
				titleToday: "Policy expires today",
				titlePast: "Policy expired",
				textFuture:
					"Your car insurance expires {{days}}. Without renewal you may not drive the vehicle (liability) or you lose damage coverage (comprehensive). We recommend acting in advance.",
				textToday:
					"Your car insurance expires today. Without renewal you lose your mandatory or comprehensive coverage. Renew the policy as soon as possible.",
				textPast:
					"Your car insurance has expired. You are currently not insured. We recommend acting without delay.",
			},
			[EventClassificationEnum.CarInsuranceAnniversary]: {
				titleFuture: "Policy anniversary {{days}}",
				titleToday: "Policy anniversary today",
				titlePast: "Policy anniversary has passed",
				textFuture:
					"{{daysCap}} your car insurance anniversary occurs. The anniversary is the ideal time to compare market offers and recalculate the premium.",
				textToday:
					"Your car insurance anniversary is today. Use the opportunity to recalculate the terms.",
				textPast:
					"Your car insurance anniversary recently passed. Consider recalculating the terms with your advisor.",
			},
			[EventClassificationEnum.PropertyInsuranceEnd]: {
				titleFuture: "Policy renewal {{days}}",
				titleToday: "Policy expires today",
				titlePast: "Policy expired",
				textFuture:
					"Your property insurance expires {{days}}. Without renewal your property will not be insured. We recommend acting in advance.",
				textToday:
					"Your property insurance expires today. Renew the policy as soon as possible to keep your coverage.",
				textPast:
					"Your property insurance expired {{date}}. Your property is currently not insured. We recommend acting without delay.",
			},
			[EventClassificationEnum.PropertyInsuranceAnniversary]: {
				titleFuture: "Policy anniversary {{days}}",
				titleToday: "Policy anniversary today",
				titlePast: "Policy anniversary has passed",
				textFuture:
					"{{daysCap}} your property insurance anniversary occurs. The anniversary is the ideal time to recalculate the terms and verify that the sum insured still matches the current value of the property.",
				textToday:
					"Your property insurance anniversary is today. Use the opportunity to recalculate the terms.",
				textPast:
					"Your property insurance anniversary recently passed. Consider recalculating the terms with your advisor.",
			},
			[EventClassificationEnum.TravelInsuranceLongEnd]: {
				titleFuture: "Long-term travel insurance expires {{days}}",
				titleToday: "Travel insurance expires today",
				titlePast: "Travel insurance expired",
				textFuture:
					"Your long-term travel insurance expires {{days}}. If you plan to travel, we recommend arranging renewal well in advance.",
				textToday:
					"Your travel insurance expires today. Arrange renewal to keep coverage while travelling.",
				textPast:
					"Your long-term travel insurance has expired. If you plan to travel, arrange new insurance.",
			},
			[EventClassificationEnum.TravelInsuranceShortEnd]: {
				titleFuture: "Travel insurance expires {{days}}",
				titleToday: "Travel insurance expires today",
				titlePast: "Travel insurance expired",
				textFuture: "Your travel insurance expires {{days}}.",
				textToday: "Your travel insurance expires today.",
				textPast:
					"Your travel insurance has expired. If you travel, arrange a new one.",
			},
			[EventClassificationEnum.AccidentHealthInsuranceEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"Your policy expires {{days}}. Contact your advisor for renewal or arranging an alternative contract.",
				textToday: "Your policy expires today. Contact your advisor.",
				textPast: "Your policy has expired. Contact your advisor.",
			},
			[EventClassificationEnum.AccidentHealthInsuranceAnniversary]: {
				titleFuture: "Policy anniversary {{days}}",
				titleToday: "Policy anniversary today",
				titlePast: "Policy anniversary has passed",
				textFuture:
					"{{daysCap}} your policy anniversary occurs. Consider consulting the terms with your advisor.",
				textToday:
					"Your policy anniversary is today. Consider consulting the terms.",
				textPast:
					"Your policy anniversary recently passed. Consider consulting the terms.",
			},
			[EventClassificationEnum.LeasingEnd]: {
				titleFuture: "Leasing ends {{days}}",
				titleToday: "Leasing contract expires today",
				titlePast: "Leasing contract expired",
				textFuture:
					"Your leasing contract expires {{days}}. Contact your advisor for instructions on buying out the vehicle, returning it, or arranging a new lease.",
				textToday:
					"Your leasing contract expires today. Contact your advisor for instructions.",
				textPast:
					"Your leasing contract has expired. Contact your advisor for instructions on the next steps.",
			},
			[EventClassificationEnum.CryptocurrencyEnd]: {
				titleFuture: "Contract ends {{days}}",
				titleToday: "Contract expires today",
				titlePast: "Contract expired",
				textFuture:
					"Your cryptocurrency contract expires {{days}}. Contact your advisor for instructions.",
				textToday: "Your contract expires today. Contact your advisor.",
				textPast: "Your contract expired {{date}}. Contact your advisor.",
			},
		},
	},
};
